import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Disc,
  Flame,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  Printer,
  Layers,
  ArrowRight,
  Package,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  Sparkles,
  Plus,
  Minus,
  Gem,
  Archive,
  History,
  Edit3,
  Check,
  X,
  Trash2,
  Boxes,
  MapPin,
  TrendingUp,
  AlertTriangle,
  Settings2,
  LayoutGrid,
  List,
  SlidersHorizontal,
  Thermometer,
  Scale,
  CheckSquare,
  FileText
} from "lucide-react";
import EngravingDisplay from "./EngravingDisplay";
import { getBaseSku, cleanSku, resolveModeloJoia, MODELOS_CANONICOS, normalizeModeloNome, isModeloComMedida } from "../../utils/skuUtils";
import { avaliarDisponibilidadeMolde } from "../../utils/moldRingUtils";
import { PRODUCTION_STATUS_CONFIG, isStatusAguardandoInjecao, isStatusFaseCeraMolde } from "../../config/productionStatuses";
import { useCommunication } from "../../contexts/CommunicationContext";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { useProductionContext } from "../../contexts/ProductionContext";
import { produtoDadosService, extractSubestoqueCera, extractConfigBorracha } from "../../services/produtoDadosService";
import DaysBadge from "./DaysBadge";
import ConfigBorrachaModal from "../modals/ConfigBorrachaModal";
import NovoMoldeModal from "../modals/NovoMoldeModal";
import EnviarCerasFundicaoModal from "../modals/EnviarCerasFundicaoModal";
import TextModal from "../modals/TextModal";
import { formatProductionTicket } from "../../utils/printFormatter";

/**
 * SeparacaoMoldesSubTab.js — Painel Especializado de Separação & Injeção de Moldes de Borracha
 * 
 * Agora com:
 * 1. Fila de Separação (Demanda de Pedidos com Subestoque de Ceras acoplado)
 * 2. Central de Monitoramento Geral de Todos os Moldes da Fábrica
 * 3. Gestão de Cera Bruta e Cera Pré-Cravada (Blindagem anti-furo com histórico auditado)
 */
export default function SeparacaoMoldesSubTab({
  user,
  orders = [],
  findCatalogItem,
  onMoveStatus,
  isMoldeSku
}) {
  const { openQuickProduct, openQuickOrder } = useCommunication();
  const { getSubestoqueCera, subestoqueCeraMap } = useCatalogContext();
  const prodContext = useProductionContext();
  const contextOrders = prodContext?.orders;
  const refreshOrders = prodContext?.refreshOrders;

  // Fonte de dados unificada e estado local otimista para resposta imediata
  const sourceOrders = useMemo(() => {
    return orders && orders.length > 0 ? orders : (contextOrders || []);
  }, [orders, contextOrders]);

  const [localOrders, setLocalOrders] = useState(sourceOrders);

  useEffect(() => {
    setLocalOrders(sourceOrders);
  }, [sourceOrders]);

  // Visão principal: "demanda" (Fila de Pedidos) ou "central_estoque" (Todos os Moldes)
  const [mainView, setMainView] = useState("demanda");

  // Filtros da Fila de Demanda
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pendentes"); // "pendentes", "todos", "fundicao"
  const [viewMode, setViewMode] = useState("moldes"); // "moldes" ou "pedidos"
  const [movingId, setMovingId] = useState(null);
  const [expandedMolds, setExpandedMolds] = useState({});
  const [printContent, setPrintContent] = useState(null);

  // Central Geral de Moldes
  const [allMoldesList, setAllMoldesList] = useState([]);
  const [loadingAllMoldes, setLoadingAllMoldes] = useState(false);
  const [searchTermCentral, setSearchTermCentral] = useState("");
  const [stockFilterCentral, setStockFilterCentral] = useState("todos"); // "todos", "com_estoque", "zerado", "com_pedidos"

  // Modo de visualização da Central: "lista" ou "cards" (salvo em localStorage, padrão tablet-first: "lista")
  const [viewModeCentral, setViewModeCentral] = useState(() => {
    try {
      return localStorage.getItem("central_moldes_view_mode") || "lista";
    } catch (_) {
      return "lista";
    }
  });

  const handleToggleViewModeCentral = (mode) => {
    setViewModeCentral(mode);
    try {
      localStorage.setItem("central_moldes_view_mode", mode);
    } catch (_) {}
  };

  // Filtros por Modelo de Joia (Pingente, Anel, Berloque, etc.)
  const [modelFilterCentral, setModelFilterCentral] = useState("Todos");
  const [modelFilterDemanda, setModelFilterDemanda] = useState("Todos");

  // Modais de Controle
  const [adjustModal, setAdjustModal] = useState(null); // { isOpen, skuPai, ceraBruta, ceraPrecravada, gaveta, pedras, motivo }
  const [historyModal, setHistoryModal] = useState(null); // { isOpen, skuPai, historico }
  const [rubberConfigModal, setRubberConfigModal] = useState({ isOpen: false, skuPai: null, config: null });
  const [isNovoMoldeModalOpen, setIsNovoMoldeModalOpen] = useState(false);
  const [fundirModalItem, setFundirModalItem] = useState(null);
  const [stockUpdatingId, setStockUpdatingId] = useState(null);

  // Mapa rápido de configurações de borrachas para a Demanda
  const moldConfigsMap = useMemo(() => {
    const map = {};
    allMoldesList.forEach(m => {
      const cleanPai = getBaseSku(cleanSku(m.skuPai));
      if (cleanPai) {
        map[cleanPai] = m.config_borracha || null;
      }
    });
    return map;
  }, [allMoldesList]);

  // Função centralizada para resolver o modelo de uma joia respeitando a configuração da borracha
  const getOrderModelo = useCallback((orderOrSku) => {
    const sku = typeof orderOrSku === "string" ? orderOrSku : orderOrSku?.sku;
    if (!sku) return "Outros";
    const cleanPai = getBaseSku(cleanSku(sku));
    const config = moldConfigsMap[cleanPai];
    if (config?.tipo_modelo && String(config.tipo_modelo).trim()) {
      return normalizeModeloNome(config.tipo_modelo);
    }
    const cat = findCatalogItem ? (findCatalogItem(sku) || findCatalogItem(cleanPai)) : null;
    return resolveModeloJoia(sku, cat, config);
  }, [moldConfigsMap, findCatalogItem]);

  // Carrega todos os moldes para a Central Geral
  const loadAllMoldes = useCallback(async () => {
    setLoadingAllMoldes(true);
    try {
      const list = await produtoDadosService.getAllMoldesWithEstoque();
      setAllMoldesList(list);
    } catch (e) {
      console.warn("Erro ao carregar lista geral de moldes:", e);
    } finally {
      setLoadingAllMoldes(false);
    }
  }, []);

  useEffect(() => {
    loadAllMoldes();
  }, [loadAllMoldes]);

  // Listener para atualização em tempo real de estoque de cera
  useEffect(() => {
    const handleCeraUpdated = (e) => {
      const { skuPai, subestoque } = e.detail || {};
      if (!skuPai || !subestoque) return;
      const targetPai = getBaseSku(cleanSku(skuPai));

      setAllMoldesList(prev => prev.map(m => {
        const mPai = getBaseSku(cleanSku(m.skuPai));
        if (mPai === targetPai) {
          return { ...m, subestoque };
        }
        return m;
      }));
    };

    window.addEventListener("produto-dados-cera-updated", handleCeraUpdated);
    return () => window.removeEventListener("produto-dados-cera-updated", handleCeraUpdated);
  }, []);

  // Listener para atualização em tempo real de configuração técnica da borracha
  useEffect(() => {
    const handleConfigUpdated = (e) => {
      const { skuPai, configBorracha } = e.detail || {};
      if (!skuPai || !configBorracha) return;
      const targetPai = getBaseSku(cleanSku(skuPai));

      setAllMoldesList(prev => {
        const exists = prev.some(m => getBaseSku(cleanSku(m.skuPai)) === targetPai);
        if (!exists) {
          return [...prev, {
            skuPai: targetPai,
            temMolde: true,
            config_borracha: configBorracha,
            ficha_tecnica: { config_borracha: configBorracha }
          }];
        }
        return prev.map(m => {
          const mPai = getBaseSku(cleanSku(m.skuPai));
          if (mPai === targetPai) {
            return { 
              ...m, 
              config_borracha: configBorracha,
              ficha_tecnica: {
                ...(m.ficha_tecnica || {}),
                config_borracha: configBorracha
              }
            };
          }
          return m;
        });
      });
    };

    window.addEventListener("produto-dados-config-borracha-updated", handleConfigUpdated);
    return () => window.removeEventListener("produto-dados-config-borracha-updated", handleConfigUpdated);
  }, []);

  // Listener para atualização em tempo real de novos moldes cadastrados
  useEffect(() => {
    const handleMoldeUpdated = () => {
      loadAllMoldes();
    };
    window.addEventListener("produto-dados-molde-updated", handleMoldeUpdated);
    return () => window.removeEventListener("produto-dados-molde-updated", handleMoldeUpdated);
  }, [loadAllMoldes]);

  // 1. Filtra estritamente os pedidos que possuem molde de borracha E que estão no ciclo pré-fundição ou em fundição
  const moldeOrders = useMemo(() => {
    return localOrders.filter(order => {
      const cat = findCatalogItem ? findCatalogItem(order.sku) : null;
      const cleanPai = getBaseSku(cleanSku(order.sku));
      const has = Boolean(
        order.tem_molde ||
        order.full_data?.tem_molde ||
        order.specs?.tem_molde ||
        cat?.tem_molde ||
        cat?.full_data?.tem_molde ||
        cat?.full_data?.processo_fabricacao === "borracha" ||
        (isMoldeSku && (isMoldeSku(order.sku, { order }) || isMoldeSku(cleanPai, { order })))
      );
      if (!has) return false;

      // Validação Anti-Falso Positivo para Anéis e Alianças
      const moldConfig = moldConfigsMap[cleanPai] || cat?.full_data?.config_borracha || cat?.config_borracha;
      const avaliacao = avaliarDisponibilidadeMolde(order, moldConfig, { catItem: cat });
      if (!avaliacao.disponivel) {
        return false;
      }

      // Inclui pedidos em injeção (aguardando), injetados (aguardando árvore) ou recém-enviados para fundição
      return isStatusAguardandoInjecao(order.status) || order.status === "INJETADO" || order.status === "FUNDICAO";
    });
  }, [localOrders, findCatalogItem, isMoldeSku, moldConfigsMap]);

  // Totais consolidados de status por Molde Físico (SKU Pai) em todo o fluxo de cera/molde
  const moldCountsMap = useMemo(() => {
    const counts = {};
    moldeOrders.forEach(order => {
      const clean = cleanSku(order.sku);
      const skuPai = getBaseSku(clean) || clean;
      if (!counts[skuPai]) {
        counts[skuPai] = { total: 0, pending: 0, injetados: 0, fundicao: 0 };
      }
      counts[skuPai].total += 1;
      if (isStatusAguardandoInjecao(order.status)) {
        counts[skuPai].pending += 1;
      } else if (order.status === "INJETADO") {
        counts[skuPai].injetados += 1;
      } else if (order.status === "FUNDICAO") {
        counts[skuPai].fundicao += 1;
      }
    });
    return counts;
  }, [moldeOrders]);

  // 2. Aplica filtros de busca, etapa e modelo na fila de demanda
  const filteredOrders = useMemo(() => {
    return moldeOrders.filter(order => {
      // Filtro de Etapa
      if (statusFilter === "pendentes") {
        if (!isStatusAguardandoInjecao(order.status)) {
          return false;
        }
      } else if (statusFilter === "injetados") {
        if (order.status !== "INJETADO") return false;
      } else if (statusFilter === "fundicao") {
        if (order.status !== "FUNDICAO") return false;
      }

      // Filtro de Modelo de Joia
      if (modelFilterDemanda !== "Todos") {
        const mod = getOrderModelo(order);
        if (mod !== modelFilterDemanda) return false;
      }

      // Filtro de Texto
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      const sku = (order.sku || "").toLowerCase();
      const orderNum = String(order.order?.number || "").toLowerCase();
      const client = (order.order?.customer?.name || "").toLowerCase();
      const cat = findCatalogItem ? findCatalogItem(order.sku) : null;
      const prodName = (cat?.name || "").toLowerCase();

      return sku.includes(term) || orderNum.includes(term) || client.includes(term) || prodName.includes(term);
    });
  }, [moldeOrders, statusFilter, modelFilterDemanda, searchTerm, getOrderModelo, findCatalogItem]);

  // 3. Agrupamento inteligente por Molde Físico (SKU Pai)
  const moldGroups = useMemo(() => {
    const groups = {};
    filteredOrders.forEach(order => {
      const clean = cleanSku(order.sku);
      const skuPai = getBaseSku(clean) || clean;
      if (!groups[skuPai]) {
        const cat = findCatalogItem ? findCatalogItem(skuPai) : null;
        const counts = moldCountsMap[skuPai] || { total: 0, pending: 0, injetados: 0, fundicao: 0 };
        groups[skuPai] = {
          skuPai,
          catalog: cat,
          orders: [],
          totalQtd: counts.total,
          pendingQtd: counts.pending,
          injetadosQtd: counts.injetados,
          fundicaoQtd: counts.fundicao,
        };
      }
      groups[skuPai].orders.push(order);
    });

    return Object.values(groups).sort((a, b) => b.pendingQtd - a.pendingQtd || b.injetadosQtd - a.injetadosQtd || b.totalQtd - a.totalQtd);
  }, [filteredOrders, moldCountsMap, findCatalogItem]);

  // Mapa de demanda em tempo real por SKU Pai (para a Central Geral)
  const demandMap = useMemo(() => {
    const map = {};
    moldeOrders.forEach(order => {
      if (isStatusAguardandoInjecao(order.status)) {
        const cleanPai = getBaseSku(cleanSku(order.sku));
        if (cleanPai) {
          map[cleanPai] = (map[cleanPai] || 0) + 1;
        }
      }
    });
    return map;
  }, [moldeOrders]);

  // KPIs da Fila de Demanda
  const totalMoldes = moldeOrders.length;
  const aguardandoInjecao = moldeOrders.filter(o => isStatusAguardandoInjecao(o.status)).length;
  const injetadosQtd = moldeOrders.filter(o => o.status === "INJETADO").length;
  const emFundicao = moldeOrders.filter(o => o.status === "FUNDICAO").length;

  // KPIs da Central Geral de Estoque de Ceras
  const kpisCentral = useMemo(() => {
    let totalCerasBrutas = 0;
    let totalCerasPrecravadas = 0;
    let moldesComEstoque = 0;
    let moldesZerados = 0;

    allMoldesList.forEach(m => {
      const est = getSubestoqueCera(m.skuPai) || m.subestoque || {};
      const bruta = Number(est.cera_bruta) || 0;
      const precravada = Number(est.cera_precravada) || 0;
      const total = bruta + precravada;

      totalCerasBrutas += bruta;
      totalCerasPrecravadas += precravada;

      if (total > 0) moldesComEstoque++;
      else moldesZerados++;
    });

    return {
      totalCeras: totalCerasBrutas + totalCerasPrecravadas,
      totalCerasBrutas,
      totalCerasPrecravadas,
      moldesComEstoque,
      moldesZerados,
      totalMoldesAtivos: allMoldesList.length
    };
  }, [allMoldesList, getSubestoqueCera]);

  // Lista filtrada da Central Geral de Moldes
  const filteredCentralMoldes = useMemo(() => {
    return allMoldesList.filter(item => {
      const skuPai = item.skuPai;
      const est = getSubestoqueCera(skuPai) || item.subestoque || {};
      const bruta = Number(est.cera_bruta) || 0;
      const precravada = Number(est.cera_precravada) || 0;
      const total = bruta + precravada;
      const demanda = demandMap[skuPai] || 0;

      // Filtro de Estado de Estoque
      if (stockFilterCentral === "com_estoque" && total === 0) return false;
      if (stockFilterCentral === "zerado" && total > 0) return false;
      if (stockFilterCentral === "com_pedidos" && demanda === 0) return false;

      // Filtro de Modelo de Joia
      if (modelFilterCentral !== "Todos") {
        const cat = findCatalogItem ? findCatalogItem(skuPai) : null;
        const mod = item.config_borracha?.tipo_modelo || resolveModeloJoia(skuPai, cat, item);
        if (mod !== modelFilterCentral) return false;
      }

      // Filtro de Busca
      if (!searchTermCentral.trim()) return true;
      const term = searchTermCentral.toLowerCase();
      const cat = findCatalogItem ? findCatalogItem(skuPai) : null;
      const name = (cat?.name || item.ficha_tecnica?.nome || "").toLowerCase();
      const gaveta = (est.localizacao_gaveta || "").toLowerCase();
      const codigo = (item.config_borracha?.codigo_maquina || "").toLowerCase();

      return skuPai.toLowerCase().includes(term) || name.includes(term) || gaveta.includes(term) || codigo.includes(term);
    });
  }, [allMoldesList, stockFilterCentral, modelFilterCentral, searchTermCentral, demandMap, getSubestoqueCera, findCatalogItem]);

  // Contagens por Modelo para Fila de Demanda
  const demandaModelCounts = useMemo(() => {
    const counts = { Todos: moldeOrders.length };
    moldeOrders.forEach(o => {
      const mod = getOrderModelo(o);
      counts[mod] = (counts[mod] || 0) + 1;
    });
    return counts;
  }, [moldeOrders, getOrderModelo]);

  // Contagens por Modelo para Central de Moldes
  const centralModelCounts = useMemo(() => {
    const counts = { Todos: allMoldesList.length };
    allMoldesList.forEach(m => {
      const cat = findCatalogItem ? findCatalogItem(m.skuPai) : null;
      const mod = m.config_borracha?.tipo_modelo || resolveModeloJoia(m.skuPai, cat, m);
      counts[mod] = (counts[mod] || 0) + 1;
    });
    return counts;
  }, [allMoldesList, findCatalogItem]);

  // =========================================================================
  // HANDLERS DE AÇÃO NO SUBESTOQUE
  // =========================================================================

  // Ajuste rápido de delta (+1, -1, +5, +10)
  const handleQuickDeltaStock = async (skuPai, deltaBruta = 0, deltaPrecravada = 0, motivo = "Ajuste rápido") => {
    setStockUpdatingId(skuPai);
    try {
      await produtoDadosService.updateSubestoqueCera(skuPai, {
        deltaBruta,
        deltaPrecravada,
        tipoOperacao: deltaBruta > 0 || deltaPrecravada > 0 ? "ENTRADA_LOTE" : "SAIDA_MANUAL",
        motivo,
        usuario: user?.name || "Operador"
      });
    } catch (e) {
      alert("Erro ao atualizar cera: " + e.message);
    } finally {
      setStockUpdatingId(null);
    }
  };

  // Converte 1 cera bruta em pré-cravada (colocou pedras na cera)
  const handleConvertBrutaToPrecravada = async (skuPai) => {
    const est = getSubestoqueCera(skuPai);
    if ((est.cera_bruta || 0) <= 0) {
      alert("Não há cera bruta disponível para pré-cravar.");
      return;
    }
    await handleQuickDeltaStock(skuPai, -1, +1, "Cera bruta pré-cravada com pedras");
  };

  // Abre o Modal de Ajuste/Inventário com suporte a Grade de Aros para Anéis
  const openAdjustModal = useCallback((skuPai, est, modeloParam = null) => {
    const cleanPai = getBaseSku(cleanSku(skuPai)) || skuPai;
    const cat = findCatalogItem ? findCatalogItem(cleanPai) : null;
    const config = moldConfigsMap[cleanPai] || {};
    const modelo = modeloParam || config?.tipo_modelo || resolveModeloJoia(cleanPai, cat, null);
    const arosCadastrados = Array.isArray(config?.aros_disponiveis) ? config.aros_disponiveis : [];
    const existingPorAro = est?.estoque_por_aro || {};
    const isMedida = isModeloComMedida(modelo) || arosCadastrados.length > 0 || Object.keys(existingPorAro).length > 0;

    const initialEstoquePorAro = {};

    // 1. Inclui aros cadastrados na configuração da borracha
    arosCadastrados.forEach(a => {
      const cleanA = String(a).trim();
      if (cleanA) {
        initialEstoquePorAro[cleanA] = {
          bruta: Number(existingPorAro[cleanA]?.bruta) || 0,
          precravada: Number(existingPorAro[cleanA]?.precravada) || 0
        };
      }
    });

    // 2. Inclui aros já existentes com estoque/histórico
    Object.entries(existingPorAro).forEach(([k, val]) => {
      const cleanA = String(k).trim();
      if (cleanA) {
        initialEstoquePorAro[cleanA] = {
          bruta: Number(val?.bruta) || 0,
          precravada: Number(val?.precravada) || 0
        };
      }
    });

    // 3. Fallback inicial para anéis sem aros configurados
    if (isMedida && Object.keys(initialEstoquePorAro).length === 0) {
      [12, 14, 16, 18, 20, 22, 24].forEach(a => {
        initialEstoquePorAro[String(a)] = { bruta: 0, precravada: 0 };
      });
    }

    setAdjustModal({
      isOpen: true,
      skuPai: cleanPai,
      ceraBruta: est?.cera_bruta || 0,
      ceraPrecravada: est?.cera_precravada || 0,
      gaveta: est?.localizacao_gaveta || "",
      pedras: est?.pedras_padrao || "",
      motivo: "",
      isMedida,
      mode: isMedida ? "grade" : "global",
      estoquePorAro: initialEstoquePorAro,
      newAroInput: ""
    });
  }, [findCatalogItem, moldConfigsMap]);

  // Salva ajuste físico / inventário via Modal
  const handleSaveAdjustModal = async () => {
    if (!adjustModal) return;
    const { skuPai, ceraBruta, ceraPrecravada, gaveta, pedras, motivo, mode, estoquePorAro } = adjustModal;
    setStockUpdatingId(skuPai);
    try {
      if (mode === "grade") {
        await produtoDadosService.updateSubestoqueCera(skuPai, {
          estoquePorAro,
          localizacao_gaveta: (gaveta || "").trim(),
          pedras_padrao: pedras,
          tipoOperacao: "INVENTARIO_AJUSTE",
          motivo: motivo || "Inventário físico por numeração de aro",
          usuario: user?.name || "Operador"
        });
      } else {
        await produtoDadosService.updateSubestoqueCera(skuPai, {
          newBruta: Math.max(0, parseInt(ceraBruta, 10) || 0),
          newPrecravada: Math.max(0, parseInt(ceraPrecravada, 10) || 0),
          localizacao_gaveta: (gaveta || "").trim(),
          pedras_padrao: pedras,
          tipoOperacao: "INVENTARIO_AJUSTE",
          motivo: motivo || "Conferência física da gaveta",
          usuario: user?.name || "Operador"
        });
      }
      setAdjustModal(null);
    } catch (e) {
      alert("Erro ao salvar inventário: " + e.message);
    } finally {
      setStockUpdatingId(null);
    }
  };

  // Move pedido individual para INJETADO (opcionalmente consumindo cera do subestoque)
  const handleMoveOrderToInjetado = async (order, consumeStock = false, tipoCera = "bruta") => {
    if (!onMoveStatus) return;
    const cleanPai = getBaseSku(cleanSku(order.sku));

    const msg = consumeStock
      ? `Confirmar atendimento do Pedido #${order.order?.number || order.sku} para "INJETADO"?\n\n(Dará baixa de 1 Cera ${tipoCera === "precravada" ? "Pré-Cravada" : "Bruta"} da gaveta. A peça aguardará montagem na árvore para Fundição)`
      : `Confirmar injeção do item ${order.sku} para "INJETADO"?\n\n(A peça foi injetada em cera e aguardará montagem na árvore de fundição)`;

    if (!window.confirm(msg)) return;

    setMovingId(order.id);

    // ATUALIZAÇÃO OTIMISTA LOCAL IMEDIATA NA TELA (0ms de espera)
    setLocalOrders(prev => prev.map(o =>
      String(o.id) === String(order.id)
        ? {
            ...o,
            status: "INJETADO",
            production_step: "INJETADO",
            full_data: { ...(o.full_data || {}), status: "INJETADO" },
            updated: new Date().toISOString()
          }
        : o
    ));

    try {
      await onMoveStatus(order.id, "INJETADO");
      if (consumeStock) {
        await produtoDadosService.consumeCeraForOrder(cleanPai, {
          tipoCera,
          aro: order.specs?.size || null,
          orderNumber: order.order?.number || "AVULSO",
          usuario: user?.name || "Operador"
        });
      }
      if (refreshOrders) {
        refreshOrders().catch(() => {});
      }
    } catch (e) {
      // Reverte em caso de erro
      setLocalOrders(sourceOrders);
      alert("Erro ao processar injeção: " + e.message);
    } finally {
      setMovingId(null);
    }
  };

  // Move grupo do molde em lote para INJETADO (opcionalmente com baixa de estoque)
  const handleBatchMoveGroupToInjetado = async (group, consumeStock = false) => {
    const pendentes = group.orders.filter(o => isStatusAguardandoInjecao(o.status));
    if (pendentes.length === 0) {
      alert("Não há pedidos pendentes de injeção neste molde.");
      return;
    }

    const msg = consumeStock
      ? `Confirmar atendimento de TODAS as ${pendentes.length} peças do molde ${group.skuPai} para "INJETADO"?\n\n(Serão consumidas ${pendentes.length} ceras do estoque da gaveta. Elas aguardarão montagem na árvore)`
      : `Confirmar marcação de TODAS as ${pendentes.length} peças do molde ${group.skuPai} como "INJETADO"?`;

    if (!window.confirm(msg)) return;

    const pendentesIds = new Set(pendentes.map(p => String(p.id)));
    setMovingId(group.skuPai);

    // ATUALIZAÇÃO OTIMISTA LOCAL IMEDIATA DO LOTE INTEIRO (0ms de espera)
    setLocalOrders(prev => prev.map(o =>
      pendentesIds.has(String(o.id))
        ? {
            ...o,
            status: "INJETADO",
            production_step: "INJETADO",
            full_data: { ...(o.full_data || {}), status: "INJETADO" },
            updated: new Date().toISOString()
          }
        : o
    ));

    try {
      for (const ord of pendentes) {
        await onMoveStatus(ord.id, "INJETADO");
        if (consumeStock) {
          const curr = getSubestoqueCera(group.skuPai);
          const pref = curr.cera_bruta > 0 ? "bruta" : "precravada";
          await produtoDadosService.consumeCeraForOrder(group.skuPai, {
            tipoCera: pref,
            orderNumber: ord.order?.number || "AVULSO",
            usuario: user?.name || "Operador"
          });
        }
      }
      if (refreshOrders) {
        refreshOrders().catch(() => {});
      }
      alert(`${pendentes.length} peças do molde ${group.skuPai} foram movidas para INJETADO!`);
    } catch (e) {
      setLocalOrders(sourceOrders);
      alert("Erro ao mover itens: " + e.message);
    } finally {
      setMovingId(null);
    }
  };

  // Avança pedido individual de INJETADO para FUNDIÇÃO (montado na árvore de fundição)
  const handleMoveOrderToFundicao = async (order) => {
    if (!onMoveStatus) return;
    const msg = `Confirmar que a peça do Pedido #${order.order?.number || order.sku} foi montada na árvore/cilindro e enviada para "FUNDIÇÃO"?`;
    if (!window.confirm(msg)) return;

    setMovingId(order.id);

    // ATUALIZAÇÃO OTIMISTA LOCAL IMEDIATA
    setLocalOrders(prev => prev.map(o =>
      String(o.id) === String(order.id)
        ? {
            ...o,
            status: "FUNDICAO",
            production_step: "FUNDICAO",
            full_data: { ...(o.full_data || {}), status: "FUNDICAO" },
            updated: new Date().toISOString()
          }
        : o
    ));

    try {
      await onMoveStatus(order.id, "FUNDICAO");
      if (refreshOrders) {
        refreshOrders().catch(() => {});
      }
    } catch (e) {
      setLocalOrders(sourceOrders);
      alert("Erro ao enviar para Fundição: " + e.message);
    } finally {
      setMovingId(null);
    }
  };

  // Avança em lote todas as peças injetadas do molde para FUNDIÇÃO (montadas na árvore)
  const handleBatchMoveGroupToFundicao = async (group) => {
    const injetados = group.orders.filter(o => o.status === "INJETADO");
    if (injetados.length === 0) {
      alert("Não há pedidos com status INJETADO neste molde.");
      return;
    }

    const msg = `Confirmar que as ${injetados.length} peças injetadas do molde ${group.skuPai} foram montadas na árvore e enviadas para "FUNDIÇÃO"?`;
    if (!window.confirm(msg)) return;

    const injetadosIds = new Set(injetados.map(i => String(i.id)));
    setMovingId(group.skuPai);

    // ATUALIZAÇÃO OTIMISTA LOCAL IMEDIATA DO LOTE PARA FUNDIÇÃO
    setLocalOrders(prev => prev.map(o =>
      injetadosIds.has(String(o.id))
        ? {
            ...o,
            status: "FUNDICAO",
            production_step: "FUNDICAO",
            full_data: { ...(o.full_data || {}), status: "FUNDICAO" },
            updated: new Date().toISOString()
          }
        : o
    ));

    try {
      for (const ord of injetados) {
        await onMoveStatus(ord.id, "FUNDICAO");
      }
      if (refreshOrders) {
        refreshOrders().catch(() => {});
      }
      alert(`${injetados.length} peças montadas na árvore e movidas para FUNDIÇÃO!`);
    } catch (e) {
      setLocalOrders(sourceOrders);
      alert("Erro ao mover para Fundição: " + e.message);
    } finally {
      setMovingId(null);
    }
  };

  // Alterna expansão de um molde
  const toggleMoldExpand = (skuPai) => {
    setExpandedMolds(prev => ({
      ...prev,
      [skuPai]: prev[skuPai] === false ? true : !prev[skuPai]
    }));
  };

  // Adapta um item/pedido para o formato padrão esperado pelo formatProductionTicket
  const adaptOrderForProductionTicket = useCallback((order) => {
    const fullData = order.full_data || {};
    const orderNum = order.order?.number || order.order_number || fullData.order?.number || order.orderNumber || order.sku || "AVULSO";
    const customerName = order.customerName || order.customer_name || order.order?.customer?.name || fullData.order?.customer?.name || fullData.customer_name || "Solicitação-Estoque";
    const rawSpecs = order.specs || fullData.specs || {};

    // Data de entrada sanitizada
    let rawDate = order.dateStr || order.dateIn || order.createdAt || fullData.createdAt || order.created;
    let safeString = "";
    try {
      if (rawDate && typeof rawDate.toDate === "function") {
        safeString = rawDate.toDate().toLocaleString("pt-BR");
      } else if (rawDate instanceof Date) {
        safeString = rawDate.toLocaleString("pt-BR");
      } else if (typeof rawDate === "string") {
        if (rawDate.includes("T")) {
          safeString = new Date(rawDate).toLocaleString("pt-BR");
        } else {
          safeString = rawDate;
        }
      } else {
        safeString = new Date().toLocaleString("pt-BR");
      }
    } catch (_) {
      safeString = new Date().toLocaleString("pt-BR");
    }

    // Lote de Produção da Fábrica
    let rawBatch = order.loteCodigo || order.lote_codigo || order.loteName || fullData.loteCodigo;
    if (!rawBatch && order.lote && !String(order.lote).match(/^[a-z0-9]{15}$/i)) {
      rawBatch = order.lote;
    }

    return {
      ...order,
      sku: order.sku || fullData.sku || "-",
      customerName,
      loteCodigo: rawBatch || "AVULSO",
      order: {
        number: orderNum,
        customer: {
          name: customerName,
        },
      },
      dateStr: safeString,
      specs: {
        ...rawSpecs,
        size: rawSpecs.size || order.size || fullData.size || "-",
        stoneType: rawSpecs.stoneType || rawSpecs.pedra || rawSpecs.tipo_cera || "-",
        stoneColor: rawSpecs.stoneColor || rawSpecs.cor || "-",
        stoneBatch: rawSpecs.stoneBatch || rawSpecs.lotePedra || "-",
        finishing: rawSpecs.finishing || rawSpecs.acabamento || "-",
        engraving: rawSpecs.engraving || rawSpecs.gravacao || "-",
        jewelryType: rawSpecs.jewelryType || rawSpecs.tipo || "Borracha",
        material: rawSpecs.material || rawSpecs.metal || "Prata 925",
        category: rawSpecs.category || rawSpecs.categoria || "-",
      },
    };
  }, []);

  // Impressão de Filipetas no formato padrão do fluxo de produção (térmica 80mm / tickets)
  const handlePrintFilipetas = useCallback((targetOrders = filteredOrders) => {
    if (!targetOrders || targetOrders.length === 0) {
      alert("Nenhum pedido ou solicitação disponível para imprimir filipetas.");
      return;
    }

    const safeItems = targetOrders.map(adaptOrderForProductionTicket);
    const fullText = formatProductionTicket(safeItems);

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      // Se popup estiver bloqueado, exibe no TextModal como fallback imediato
      setPrintContent(fullText);
      return;
    }

    const ticketsHtml = safeItems.map((item) => {
      const ticketText = formatProductionTicket([item]);
      return `
        <div class="ticket-wrapper">
          <pre class="ticket-body">${ticketText.trim()}</pre>
        </div>
      `;
    }).join("\n");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Filipetas de Produção (${safeItems.length} pedidos) - Sempre Joias</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0;
          }
          @media print {
            .no-print { display: none !important; }
            body { margin: 0; padding: 0; background: #fff; }
            .ticket-wrapper {
              page-break-after: always;
              break-after: page;
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 2mm 3mm !important;
            }
          }
          body {
            font-family: 'Courier New', Courier, monospace, ui-monospace;
            font-size: 13px;
            line-height: 1.25;
            font-weight: 600;
            color: #000;
            background-color: #f1f5f9;
            margin: 0;
            padding: 16px;
          }
          .toolbar {
            position: sticky;
            top: 0;
            background: #1e293b;
            color: white;
            padding: 10px 16px;
            margin-bottom: 16px;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 4px 6px rgba(0,0,0,0.15);
            font-family: system-ui, -apple-system, sans-serif;
            z-index: 100;
          }
          .btn {
            padding: 6px 14px;
            font-weight: bold;
            border-radius: 6px;
            cursor: pointer;
            border: none;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;
          }
          .btn-print { background: #2563eb; color: white; }
          .btn-print:hover { background: #1d4ed8; }
          .btn-copy { background: #475569; color: white; }
          .btn-copy:hover { background: #334155; }
          .btn-close { background: transparent; color: #94a3b8; border: 1px solid #475569; }
          .btn-close:hover { background: #334155; color: white; }
          .tickets-container {
            max-width: 320px;
            margin: 0 auto;
          }
          .ticket-wrapper {
            background: #fff;
            border: 1px dashed #94a3b8;
            border-radius: 4px;
            margin-bottom: 16px;
            padding: 12px 14px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          }
          .ticket-body {
            margin: 0;
            padding: 0;
            font-family: 'Courier New', Courier, monospace, ui-monospace;
            font-size: 12.5px;
            line-height: 1.25;
            white-space: pre-wrap;
            word-break: break-word;
            color: #0f172a;
          }
        </style>
      </head>
      <body>
        <div class="toolbar no-print">
          <div style="font-size: 13px; font-weight: bold;">
            🏷️ Filipetas de Produção (${safeItems.length} ${safeItems.length === 1 ? "pedido" : "pedidos"})
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn btn-copy" onclick="copyAll()">📋 Copiar Texto</button>
            <button class="btn btn-print" onclick="window.print()">🖨️ Imprimir</button>
            <button class="btn btn-close" onclick="window.close()">✕ Fechar</button>
          </div>
        </div>
        <div class="tickets-container">
          ${ticketsHtml}
        </div>
        <script>
          function copyAll() {
            const text = ${JSON.stringify(fullText)};
            navigator.clipboard.writeText(text).then(function() {
              alert("Texto das filipetas copiado com sucesso!");
            });
          }
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 350);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  }, [filteredOrders, adaptOrderForProductionTicket]);

  // Impressão da folha de separação
  const handlePrintSeparationSheet = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return alert("Habilite popups no navegador para imprimir a folha de separação.");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Folha de Separação de Moldes de Borracha - Sempre Joias</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; color: #1e293b; }
          h1 { font-size: 18px; margin-bottom: 4px; text-transform: uppercase; }
          .meta { font-size: 11px; color: #64748b; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
          th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; }
          th { background: #f1f5f9; font-weight: bold; text-transform: uppercase; font-size: 10px; }
          .tag { background: #fef3c7; color: #92400e; font-weight: bold; padding: 2px 6px; border-radius: 4px; border: 1px solid #fde68a; }
          .badge-qtd { font-size: 13px; font-weight: bold; }
          .check-col { width: 35px; text-align: center; }
          .checkbox-square { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #475569; border-radius: 2px; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <div style="display:flex; justify-content:space-between; align-items:flex-start;">
          <div>
            <h1>🏷️ Folha de Separação / Injeção — Moldes de Borracha</h1>
            <div class="meta">Gerado em ${new Date().toLocaleString("pt-BR")} | Total de Moldes: ${moldGroups.length} | Total de Peças: ${filteredOrders.length}</div>
          </div>
          <button onclick="window.print()" style="padding: 6px 12px; font-weight:bold; cursor:pointer;">Imprimir</button>
        </div>
        <table>
          <thead>
            <tr>
              <th class="check-col">OK</th>
              <th>Molde (SKU Pai)</th>
              <th>Gaveta / Estoque Cera</th>
              <th>Nome / Descrição</th>
              <th>A Injetar</th>
              <th>Injetadas (P/ Árvore)</th>
              <th>Pedidos & Detalhes (Aro/Pedras)</th>
            </tr>
          </thead>
          <tbody>
            ${moldGroups.map(g => {
              const est = getSubestoqueCera(g.skuPai);
              return `
              <tr>
                <td class="check-col"><div class="checkbox-square"></div></td>
                <td><strong style="font-family: monospace; font-size: 12px;">${g.skuPai}</strong></td>
                <td>
                  <strong>${est.localizacao_gaveta || "S/ Gaveta"}</strong><br/>
                  <small style="color:#64748b;">Bruta: ${est.cera_bruta} | Cravada: ${est.cera_precravada}</small>
                </td>
                <td>${g.catalog?.name || "-"}</td>
                <td><span class="badge-qtd">${g.pendingQtd || 0} un</span></td>
                <td><strong>${g.injetadosQtd || 0} un</strong></td>
                <td>
                  ${g.orders.map(o => {
                    const cleanP = getBaseSku(cleanSku(o.sku));
                    const mCfg = moldConfigsMap[cleanP] || g.catalog?.full_data?.config_borracha;
                    const av = avaliarDisponibilidadeMolde(o, mCfg);
                    let aroText = o.specs?.size ? `(Aro: ${o.specs.size})` : "";
                    if (av.tipoMatch === "ajuste_bancada") {
                      aroText = `<strong>(Aro: ${av.aroSolicitado} ⚠️ Molde ${av.aroMolde} - Ajustar)</strong>`;
                    }
                    return `
                    <div style="margin-bottom: 2px;">
                      #${o.order?.number || "AVULSO"} - <code>${o.sku}</code> 
                      ${aroText} 
                      ${o.specs?.stoneColor ? `[${o.specs.stoneColor}]` : ""}
                      <span style="font-size: 9px; color: #64748b;">(${o.status})</span>
                    </div>
                  `;}).join("")}
                </td>
              </tr>
            `;}).join("")}
          </tbody>
        </table>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-y-auto custom-scrollbar">
      
      {/* =========================================================================
          CABEÇALHO & SELETOR DE MODO (FILA DE DEMANDA vs CENTRAL DE ESTOQUE)
         ========================================================================= */}
      <div className="bg-white border-b border-slate-200 p-4 md:p-6 shrink-0 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl shadow-md">
              <Disc size={24} className="animate-spin-slow" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-black uppercase tracking-wider text-slate-800">
                  Separação & Injeção de Moldes de Borracha
                </h1>
                <span className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Fluxo Direto p/ Fundição
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Peças em molde de borracha pulam impressão 3D/cura. Injete ceras, prepare pré-cravados e despache direto para a árvore.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* SELETOR DE VISÃO PRINCIPAL */}
            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner">
              <button
                type="button"
                onClick={() => setMainView("demanda")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  mainView === "demanda"
                    ? "bg-amber-600 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Clock size={14} />
                <span>Fila de Separação ({aguardandoInjecao})</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMainView("central_estoque");
                  loadAllMoldes();
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  mainView === "central_estoque"
                    ? "bg-slate-800 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Boxes size={14} />
                <span>Central de Subestoque ({kpisCentral.totalCeras} ceras)</span>
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {mainView === "demanda" && (
                <div className="flex items-center rounded-lg shadow-sm border border-slate-700 overflow-hidden bg-slate-800">
                  <button
                    type="button"
                    onClick={() => handlePrintFilipetas(filteredOrders)}
                    className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                    title={`Imprimir Filipetas das ${filteredOrders.length} solicitações no formato padrão de produção`}
                  >
                    <Printer size={15} className="text-amber-400" />
                    <span>Imprimir Filipetas ({filteredOrders.length})</span>
                  </button>
                  <button
                    type="button"
                    onClick={handlePrintSeparationSheet}
                    className="px-2.5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white border-l border-slate-600 text-xs font-semibold transition-all cursor-pointer"
                    title="Imprimir Folha de Separação (Tabela Resumo em A4)"
                  >
                    <FileText size={14} />
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsNovoMoldeModalOpen(true)}
                className="px-3.5 py-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white rounded-lg text-xs font-bold transition-all shadow flex items-center gap-1.5 cursor-pointer shadow-amber-600/20"
                title="Cadastrar Novo Molde / Borracha para um SKU"
              >
                <Plus size={15} />
                <span>Novo Molde / Borracha</span>
              </button>
            </div>
          </div>
        </div>

        {/* =========================================================================
            CARDS DE KPIS CONTEXTUAIS
           ========================================================================= */}
        {mainView === "demanda" ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Aguardando Injeção</span>
                <Clock size={16} className="text-amber-600" />
              </div>
              <div className="text-2xl font-black text-amber-900 mt-1">
                {aguardandoInjecao}
              </div>
              <p className="text-[10px] text-amber-700/80 mt-0.5">Em verificar / análise / imprimir</p>
            </div>

            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/20 border border-amber-300 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Injetados (P/ Árvore)</span>
                <Disc size={16} className="text-amber-600" />
              </div>
              <div className="text-2xl font-black text-amber-950 mt-1">
                {injetadosQtd}
              </div>
              <p className="text-[10px] text-amber-800 mt-0.5">Ceras prontas aguardando montagem</p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50/50 border border-teal-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Em Fundição</span>
                <Flame size={16} className="text-teal-600" />
              </div>
              <div className="text-2xl font-black text-teal-900 mt-1">
                {emFundicao}
              </div>
              <p className="text-[10px] text-teal-700/80 mt-0.5">Já montados na árvore / forno</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Ceras Prontas em Fábrica</span>
                <Boxes size={16} className="text-blue-600" />
              </div>
              <div className="text-2xl font-black text-blue-900 mt-1">
                {kpisCentral.totalCeras}
              </div>
              <p className="text-[10px] text-blue-700/80 mt-0.5">{kpisCentral.totalCerasBrutas} brutas | {kpisCentral.totalCerasPrecravadas} pré-cravadas</p>
            </div>
          </div>
        ) : (
          /* KPIS DA CENTRAL GERAL DE SUBESTOQUE */
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50/50 border border-indigo-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Total Ceras em Fábrica</span>
                <Boxes size={16} className="text-indigo-600" />
              </div>
              <div className="text-2xl font-black text-indigo-900 mt-1">
                {kpisCentral.totalCeras} un
              </div>
              <p className="text-[10px] text-indigo-700/80 mt-0.5">Disponíveis em gavetas</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Ceras Brutas</span>
                <Flame size={16} className="text-amber-600" />
              </div>
              <div className="text-2xl font-black text-amber-900 mt-1">
                {kpisCentral.totalCerasBrutas} un
              </div>
              <p className="text-[10px] text-amber-700/80 mt-0.5">Injetadas limpas</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50/50 border border-purple-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Ceras Pré-Cravadas</span>
                <Gem size={16} className="text-purple-600" />
              </div>
              <div className="text-2xl font-black text-purple-900 mt-1">
                {kpisCentral.totalCerasPrecravadas} un
              </div>
              <p className="text-[10px] text-purple-700/80 mt-0.5">Com pedras na cera</p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50/50 border border-teal-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Moldes Abastecidos</span>
                <CheckCircle2 size={16} className="text-teal-600" />
              </div>
              <div className="text-2xl font-black text-teal-900 mt-1">
                {kpisCentral.moldesComEstoque}
              </div>
              <p className="text-[10px] text-teal-700/80 mt-0.5">Com pelo menos 1 cera</p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-50/50 border border-rose-200 rounded-xl p-3.5 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Moldes Zerados</span>
                <AlertTriangle size={16} className="text-rose-600" />
              </div>
              <div className="text-2xl font-black text-rose-900 mt-1">
                {kpisCentral.moldesZerados}
              </div>
              <p className="text-[10px] text-rose-700/80 mt-0.5">Sem nenhuma cera pronta</p>
            </div>
          </div>
        )}

        {/* =========================================================================
            BARRA DE FILTROS E BUSCA (DEPENDENDO DA VISÃO)
           ========================================================================= */}
        {mainView === "demanda" ? (
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between pt-2 border-t border-slate-100 flex-wrap">
            <div className="flex items-center gap-2 w-full sm:w-auto flex-1 max-w-md">
              <div className="relative w-full">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por SKU, Modelo, Cliente ou Pedido..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all uppercase"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end flex-wrap">
              {/* FILTRO POR MODELO DE JOIA */}
              <div className="flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 text-xs">
                <span className="text-[10px] font-bold text-amber-800 uppercase">Modelo:</span>
                <select
                  value={modelFilterDemanda}
                  onChange={(e) => setModelFilterDemanda(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 text-xs focus:outline-none cursor-pointer"
                >
                  {MODELOS_CANONICOS.map(m => (
                    <option key={m} value={m}>
                      {m} ({demandaModelCounts[m] || 0})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
                <button
                  onClick={() => setStatusFilter("pendentes")}
                  className={`px-3 py-1 rounded-md font-bold transition-all ${
                    statusFilter === "pendentes" 
                      ? "bg-amber-600 text-white shadow-sm" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Aguardando Injeção ({aguardandoInjecao})
                </button>
                <button
                  onClick={() => setStatusFilter("injetados")}
                  className={`px-3 py-1 rounded-md font-bold transition-all ${
                    statusFilter === "injetados" 
                      ? "bg-amber-500 text-slate-900 shadow-sm" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Injetados / P/ Árvore ({injetadosQtd})
                </button>
                <button
                  onClick={() => setStatusFilter("fundicao")}
                  className={`px-3 py-1 rounded-md font-bold transition-all ${
                    statusFilter === "fundicao" 
                      ? "bg-teal-600 text-white shadow-sm" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Em Fundição ({emFundicao})
                </button>
                <button
                  onClick={() => setStatusFilter("todos")}
                  className={`px-3 py-1 rounded-md font-bold transition-all ${
                    statusFilter === "todos" 
                      ? "bg-slate-800 text-white shadow-sm" 
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Todos ({totalMoldes})
                </button>
              </div>

              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
                <button
                  onClick={() => setViewMode("moldes")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    viewMode === "moldes" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"
                  }`}
                  title="Agrupar por Molde Físico (SKU Pai)"
                >
                  Por Molde
                </button>
                <button
                  onClick={() => setViewMode("pedidos")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    viewMode === "pedidos" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"
                  }`}
                  title="Lista Individual de Pedidos"
                >
                  Por Pedido
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* FILTROS DA CENTRAL GERAL */
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between pt-2 border-t border-slate-100 flex-wrap">
            <div className="flex items-center gap-2 w-full sm:w-auto flex-1 max-w-md">
              <div className="relative w-full">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por SKU, Modelo, Gaveta ou Código..."
                  value={searchTermCentral}
                  onChange={(e) => setSearchTermCentral(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-slate-800 focus:bg-white transition-all uppercase"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto justify-end flex-wrap">
              {/* FILTRO POR MODELO DE JOIA */}
              <div className="flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200 text-xs">
                <span className="text-[10px] font-bold text-slate-500 uppercase">Modelo:</span>
                <select
                  value={modelFilterCentral}
                  onChange={(e) => setModelFilterCentral(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 text-xs focus:outline-none cursor-pointer"
                >
                  {MODELOS_CANONICOS.map(m => (
                    <option key={m} value={m}>
                      {m} ({centralModelCounts[m] || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* FILTRO DE ESTOQUE */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
                <button
                  onClick={() => setStockFilterCentral("todos")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    stockFilterCentral === "todos" ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Todos ({allMoldesList.length})
                </button>
                <button
                  onClick={() => setStockFilterCentral("com_estoque")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    stockFilterCentral === "com_estoque" ? "bg-emerald-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Com Estoque ({kpisCentral.moldesComEstoque})
                </button>
                <button
                  onClick={() => setStockFilterCentral("zerado")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    stockFilterCentral === "zerado" ? "bg-rose-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Zerados ({kpisCentral.moldesZerados})
                </button>
                <button
                  onClick={() => setStockFilterCentral("com_pedidos")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all ${
                    stockFilterCentral === "com_pedidos" ? "bg-amber-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Com Pedidos
                </button>
              </div>

              {/* ALTERNADOR DE MODO: LISTA vs CARDS (TABLET-FIRST) */}
              <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
                <button
                  type="button"
                  onClick={() => handleToggleViewModeCentral("lista")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 ${
                    viewModeCentral === "lista" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                  title="Modo Lista (Otimizado para Tablets e Injeção)"
                >
                  <List size={13} />
                  <span>Lista</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleViewModeCentral("cards")}
                  className={`px-2.5 py-1 rounded-md font-bold transition-all flex items-center gap-1 ${
                    viewModeCentral === "cards" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                  title="Modo Grade de Cards"
                >
                  <LayoutGrid size={13} />
                  <span>Cards</span>
                </button>
              </div>

              <button
                onClick={loadAllMoldes}
                disabled={loadingAllMoldes}
                className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg border border-slate-200 transition"
                title="Recarregar dados de todos os moldes"
              >
                <RefreshCw size={14} className={loadingAllMoldes ? "animate-spin" : ""} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* =========================================================================
          CONTEÚDO PRINCIPAL (FILA DE DEMANDA vs CENTRAL DE ESTOQUE)
         ========================================================================= */}
      <div className="p-4 md:p-6 space-y-4">
        {mainView === "demanda" ? (
          /* =========================================================================
             VISÃO 1: FILA DE DEMANDA DE PEDIDOS (COM WIDGET DE ESTOQUE NO CARD)
             ========================================================================= */
          moldGroups.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400 space-y-2">
              <Disc size={40} className="mx-auto stroke-1 text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">Nenhum pedido de molde encontrado para este filtro.</p>
              <p className="text-xs text-slate-400">
                Apenas pedidos em Verificar, Aguardando Análise, Imprimir e Imprimindo são sinalizados aqui.
              </p>
            </div>
          ) : viewMode === "moldes" ? (
            /* AGRUPADO POR MOLDE */
            <div className="space-y-4">
              {moldGroups.map(group => {
                const isExpanded = expandedMolds[group.skuPai] !== false;
                const coverImg = group.catalog?.image || group.catalog?.image_url;
                const hasPending = group.pendingQtd > 0;

                // Dados de subestoque de cera deste molde
                const est = getSubestoqueCera(group.skuPai);
                const ceraBruta = Number(est.cera_bruta) || 0;
                const ceraPrecravada = Number(est.cera_precravada) || 0;
                const totalCerasDisponiveis = ceraBruta + ceraPrecravada;
                const cobreDemanda = totalCerasDisponiveis >= group.pendingQtd;
                const faltamCeras = Math.max(0, group.pendingQtd - totalCerasDisponiveis);

                return (
                  <div
                    key={group.skuPai}
                    className={`bg-white border rounded-xl shadow-sm transition-all overflow-hidden ${
                      hasPending ? "border-amber-300/80 ring-1 ring-amber-200/50" : "border-slate-200"
                    }`}
                  >
                    {/* CABEÇALHO DO MOLDE */}
                    <div
                      onClick={() => toggleMoldExpand(group.skuPai)}
                      className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/80 transition-colors"
                    >
                      <div className="flex items-start sm:items-center gap-4">
                        <button
                          type="button"
                          className="text-slate-400 hover:text-slate-700 mt-1 sm:mt-0"
                        >
                          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </button>

                        {/* FOTO DO PRODUTO */}
                        <div className="w-14 h-14 bg-slate-100 border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                          {coverImg ? (
                            <img src={coverImg} alt={group.skuPai} className="w-full h-full object-cover" />
                          ) : (
                            <Package size={22} className="text-slate-300" />
                          )}
                        </div>

                        {/* INFORMAÇÕES DO MOLDE */}
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuickProduct(group.skuPai);
                              }}
                              className="font-mono font-bold text-base text-slate-900 hover:text-amber-600 hover:underline flex items-center gap-1.5 text-left"
                              title="Abrir Ficha do Produto"
                            >
                              <span>{group.skuPai}</span>
                              <ExternalLink size={13} className="text-slate-400" />
                            </button>

                            <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm flex items-center gap-1 uppercase tracking-wider">
                              <Disc size={10} className="text-amber-100" />
                              MOLDE BORRACHA
                            </span>

                            {/* TIPO / MODELO DE JOIA */}
                            {(() => {
                              const modelo = getOrderModelo(group.skuPai);
                              return (
                                <span className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-bold px-1.5 py-0.5 rounded">
                                  {modelo}
                                </span>
                              );
                            })()}

                            {/* CÓDIGO DA MÁQUINA DE INJEÇÃO */}
                            {(() => {
                              const groupConfig = moldConfigsMap[group.skuPai] || null;
                              const codigo = groupConfig?.codigo_maquina;
                              return (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setRubberConfigModal({
                                      isOpen: true,
                                      skuPai: group.skuPai,
                                      config: groupConfig || extractConfigBorracha(null, group.catalog)
                                    });
                                  }}
                                  className={`px-2 py-0.5 rounded text-[10px] font-bold border transition flex items-center gap-1 cursor-pointer ${
                                    codigo
                                      ? "bg-amber-100 text-amber-950 border-amber-300 hover:bg-amber-200"
                                      : "bg-slate-100 text-slate-500 border-dashed border-slate-300 hover:bg-slate-200"
                                  }`}
                                  title="Clique para ver ou ajustar parâmetros da borracha na injetora"
                                >
                                  <Settings2 size={11} className={codigo ? "text-amber-700" : "text-slate-400"} />
                                  {codigo ? (
                                    <span className="font-mono tracking-wider font-black">⚙️ {codigo}</span>
                                  ) : (
                                    <span>+ Configurar Máquina</span>
                                  )}
                                </button>
                              );
                            })()}

                            {group.pendingQtd > 0 && (
                              <span className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {group.pendingQtd} a Injetar
                              </span>
                            )}

                            {group.injetadosQtd > 0 && (
                              <span className="bg-amber-500 text-white border border-amber-600 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                                <Disc size={10} />
                                {group.injetadosQtd} Injetado(s) P/ Árvore
                              </span>
                            )}

                            {group.fundicaoQtd > 0 && (
                              <span className="bg-teal-100 text-teal-800 border border-teal-300 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Flame size={10} />
                                {group.fundicaoQtd} em Fundição
                              </span>
                            )}

                            {/* GAVETA / POSIÇÃO FÍSICA */}
                            {est.localizacao_gaveta && (
                              <span className="bg-slate-100 text-slate-700 border border-slate-300 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                <MapPin size={10} className="text-slate-500" />
                                {est.localizacao_gaveta}
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-slate-600 font-medium line-clamp-1">
                            {group.catalog?.name || `Produto ${group.skuPai}`}
                          </p>
                        </div>
                      </div>

                      {/* WIDGET DE SUBESTOQUE DE CERA DO MOLDE (Informativo + Acesso ao Inventário) */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex flex-wrap items-center gap-2.5 bg-amber-50/60 border border-amber-200/80 rounded-xl p-2 shadow-2xs shrink-0"
                      >
                        {/* Cera Bruta */}
                        <div
                          className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-lg border border-amber-200 shadow-2xs"
                          title="Ceras brutas na gaveta"
                        >
                          <Flame size={13} className="text-amber-600" />
                          <span className="text-[10px] font-bold text-slate-600 uppercase">Bruta:</span>
                          <span className="text-xs font-black text-amber-900 min-w-[16px] text-center">
                            {ceraBruta}
                          </span>
                        </div>

                        {/* Cera Pré-Cravada */}
                        <div
                          className="flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-lg border border-purple-200 shadow-2xs"
                          title="Ceras pré-cravadas na gaveta"
                        >
                          <Gem size={13} className="text-purple-600" />
                          <span className="text-[10px] font-bold text-slate-600 uppercase">Cravada:</span>
                          <span className="text-xs font-black text-purple-900 min-w-[16px] text-center">
                            {ceraPrecravada}
                          </span>
                        </div>

                        {/* Botão de Inventário / Ajuste Físico de Ceras */}
                        <button
                          type="button"
                          onClick={() => openAdjustModal(group.skuPai, est)}
                          className="px-2.5 py-1 bg-white hover:bg-amber-100 text-slate-700 hover:text-amber-950 rounded-lg border border-slate-300 hover:border-amber-300 font-bold text-[11px] shadow-2xs flex items-center gap-1.5 cursor-pointer transition active:scale-95"
                          title="Abrir Inventário / Ajuste Físico de Ceras deste molde"
                        >
                          <Edit3 size={12} className="text-amber-600" />
                          <span>Ajustar / Inventário</span>
                        </button>
                      </div>

                      {/* AÇÕES EM LOTE PARA O MOLDE */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-3 self-end lg:self-center shrink-0"
                      >
                        {/* Imprimir Filipetas deste Molde */}
                        <button
                          type="button"
                          onClick={() => handlePrintFilipetas(group.orders)}
                          className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors cursor-pointer"
                          title={`Imprimir Filipetas deste Molde (${group.orders.length} solicitações)`}
                        >
                          <Printer size={14} />
                        </button>

                        <div className="text-right mr-2 hidden sm:block">
                          <div className="text-xs font-bold text-slate-700">
                            {group.orders.length} {group.orders.length === 1 ? "pedido" : "pedidos"}
                          </div>
                          {hasPending && (
                            <div className="text-[10px]">
                              {cobreDemanda ? (
                                <span className="text-emerald-600 font-bold flex items-center gap-0.5 justify-end">
                                  <CheckCircle2 size={10} /> Estoque cobre ({totalCerasDisponiveis} un)
                                </span>
                              ) : (
                                <span className="text-amber-600 font-bold">
                                  Faltam {faltamCeras} ceras
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Botões de Ação para Injeção */}
                        {hasPending && onMoveStatus && (
                          cobreDemanda ? (
                            <button
                              type="button"
                              onClick={() => handleBatchMoveGroupToInjetado(group, true)}
                              disabled={movingId === group.skuPai}
                              className="px-3.5 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg text-xs font-bold shadow transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-95"
                              title="Baixa automaticamente as ceras do estoque e move os pedidos para INJETADO"
                            >
                              {movingId === group.skuPai ? (
                                <RefreshCw size={13} className="animate-spin" />
                              ) : (
                                <Boxes size={14} className="text-emerald-200" />
                              )}
                              <span>Atender do Estoque &rarr; Injetados</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleBatchMoveGroupToInjetado(group, false)}
                              disabled={movingId === group.skuPai}
                              className="px-3.5 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg text-xs font-bold shadow transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-95"
                              title="Marca todas as peças pendentes deste molde como INJETADO"
                            >
                              {movingId === group.skuPai ? (
                                <RefreshCw size={13} className="animate-spin" />
                              ) : (
                                <Disc size={14} className="text-amber-200" />
                              )}
                              <span>Marcar Injetados ({group.pendingQtd})</span>
                            </button>
                          )
                        )}

                        {/* Botão de Montagem na Árvore para Fundição */}
                        {group.injetadosQtd > 0 && onMoveStatus && (
                          <button
                            type="button"
                            onClick={() => handleBatchMoveGroupToFundicao(group)}
                            disabled={movingId === group.skuPai}
                            className="px-3.5 py-1.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-lg text-xs font-bold shadow transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap active:scale-95"
                            title="Avança todas as peças injetadas para Fundição (montadas na árvore/cilindro)"
                          >
                            {movingId === group.skuPai ? (
                              <RefreshCw size={13} className="animate-spin" />
                            ) : (
                              <Flame size={14} className="text-amber-200" />
                            )}
                            <span>🌳 Montar Árvore ({group.injetadosQtd}) &rarr; Fundição</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ITENS EXPANDIDOS DO MOLDE */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 divide-y divide-slate-100 bg-slate-50/50">
                        {group.orders.map(order => {
                          const isPending = isStatusAguardandoInjecao(order.status);
                          const confStatus = PRODUCTION_STATUS_CONFIG[order.status] || { label: order.status, color: "bg-slate-500 text-white" };

                          // Verifica se há cera em estoque para atender este pedido (no aro específico se informado)
                          const cleanAro = order.specs?.size ? String(order.specs.size).trim() : null;
                          const aroStock = cleanAro && est.estoque_por_aro?.[cleanAro] ? est.estoque_por_aro[cleanAro] : null;
                          const canConsumeBruta = aroStock ? (Number(aroStock.bruta) || 0) > 0 : ceraBruta > 0;
                          const canConsumePrecravada = aroStock ? (Number(aroStock.precravada) || 0) > 0 : ceraPrecravada > 0;

                          return (
                            <div
                              key={order.id}
                              className="p-3 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-white transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <DaysBadge date={order.customCreatedAt || order.createdAt} order={order} />

                                <div className="space-y-0.5">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openQuickOrder(order.order?.number || order.id)}
                                      className="font-bold text-xs text-slate-800 hover:text-blue-600 hover:underline"
                                    >
                                      Pedido #{order.order?.number || "AVULSO"}
                                    </button>

                                    <span className="font-mono text-xs font-bold text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-200">
                                      {order.sku}
                                    </span>

                                    {(() => {
                                      const mCfg = moldConfigsMap[group.skuPai] || group.catalog?.full_data?.config_borracha;
                                      const av = avaliarDisponibilidadeMolde(order, mCfg);
                                      if (av.tipoMatch === "ajuste_bancada") {
                                        return (
                                          <span className="text-[11px] font-bold text-amber-900 bg-amber-100/90 border border-amber-300 px-2 py-0.5 rounded flex items-center gap-1 shadow-2xs" title={av.instrucao}>
                                            <span>Aro {av.aroSolicitado} ⚠️ [Molde {av.aroMolde}]</span>
                                          </span>
                                        );
                                      }
                                      if (av.tipoMatch === "exato") {
                                        return (
                                          <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded">
                                            Aro {av.aroSolicitado} (Molde Exato)
                                          </span>
                                        );
                                      }
                                      if (order.specs?.size) {
                                        return (
                                          <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 rounded">
                                            Aro {order.specs.size}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}

                                    {order.specs?.stoneColor && (
                                      <span className="text-[11px] font-bold text-pink-700 bg-pink-50 border border-pink-200 px-1.5 rounded">
                                        Pedra: {order.specs.stoneColor}
                                      </span>
                                    )}

                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${confStatus.color}`}>
                                      {confStatus.label}
                                    </span>
                                  </div>

                                  <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-1.5">
                                    <span>Cliente: <strong className="text-slate-700">{order.order?.customer?.name || "Não informado"}</strong></span>
                                    {order.specs?.engraving && (
                                      <>
                                        <span className="text-slate-300">|</span>
                                        <EngravingDisplay rawText={order.specs.engraving} mode="compact" />
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* AÇÃO DO ITEM INDIVIDUAL */}
                              <div className="flex items-center gap-2 self-end sm:self-center">
                                {/* Imprimir Filipeta deste Pedido */}
                                <button
                                  type="button"
                                  onClick={() => handlePrintFilipetas([order])}
                                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer"
                                  title={`Imprimir Filipeta do Pedido #${order.order?.number || order.sku}`}
                                >
                                  <Printer size={13} />
                                </button>
                                {isPending && onMoveStatus && (
                                  <>
                                    {/* Opção com baixa de cera pré-cravada (se houver estoque) */}
                                    {canConsumePrecravada && (
                                      <button
                                        type="button"
                                        onClick={() => handleMoveOrderToInjetado(order, true, "precravada")}
                                        disabled={movingId === order.id}
                                        className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                        title="Baixa 1 cera pré-cravada do estoque da gaveta e move para INJETADO"
                                      >
                                        <Gem size={12} className="text-purple-200" />
                                        <span>Usar Cravada (-1) &rarr; Injetado</span>
                                      </button>
                                    )}

                                    {/* Opção com baixa de cera bruta */}
                                    {canConsumeBruta && (
                                      <button
                                        type="button"
                                        onClick={() => handleMoveOrderToInjetado(order, true, "bruta")}
                                        disabled={movingId === order.id}
                                        className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                        title="Baixa 1 cera bruta do estoque e move para INJETADO"
                                      >
                                        <Boxes size={12} className="text-emerald-200" />
                                        <span>Usar Cera (-1) &rarr; Injetado</span>
                                      </button>
                                    )}

                                    {/* Opção normal de marcar como injetado */}
                                    <button
                                      type="button"
                                      onClick={() => handleMoveOrderToInjetado(order, false)}
                                      disabled={movingId === order.id}
                                      className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                      title="Marcar peça como injetada (aguardando montagem na árvore)"
                                    >
                                      {movingId === order.id ? (
                                        <RefreshCw size={12} className="animate-spin" />
                                      ) : (
                                        <Disc size={12} className="text-amber-200" />
                                      )}
                                      <span>💉 Marcar Injetado</span>
                                    </button>
                                  </>
                                )}

                                {/* Opção quando já está INJETADO: Montar na Árvore -> Fundição */}
                                {order.status === "INJETADO" && onMoveStatus && (
                                  <button
                                    type="button"
                                    onClick={() => handleMoveOrderToFundicao(order)}
                                    disabled={movingId === order.id}
                                    className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-[11px] font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                    title="Montar cera na árvore de fundição e avançar para Fundição"
                                  >
                                    {movingId === order.id ? (
                                      <RefreshCw size={12} className="animate-spin" />
                                    ) : (
                                      <Flame size={12} className="text-teal-200" />
                                    )}
                                    <span>🌳 Montar na Árvore &rarr; Fundição</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* MODO LISTA INDIVIDUAL DE PEDIDOS */
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 shadow-sm overflow-hidden">
              {filteredOrders.map(order => {
                const confStatus = PRODUCTION_STATUS_CONFIG[order.status] || { label: order.status, color: "bg-slate-500 text-white" };
                const isPending = isStatusAguardandoInjecao(order.status);
                const cleanPai = getBaseSku(cleanSku(order.sku));
                const est = getSubestoqueCera(cleanPai);

                return (
                  <div key={order.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/80 transition-colors">
                    <div className="flex items-center gap-3">
                      <DaysBadge date={order.customCreatedAt || order.createdAt} order={order} />

                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openQuickProduct(order.sku)}
                            className="font-mono font-bold text-sm text-slate-900 hover:text-amber-600 hover:underline"
                          >
                            {order.sku}
                          </button>

                          <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm flex items-center gap-1 uppercase tracking-wider">
                            <Disc size={10} className="text-amber-100" />
                            BORRACHA
                          </span>

                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${confStatus.color}`}>
                            {confStatus.label}
                          </span>

                          <span className="text-xs text-slate-500 font-medium">
                            Pedido #{order.order?.number || "AVULSO"}
                          </span>

                          {est.localizacao_gaveta && (
                            <span className="bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded border border-slate-200">
                              📍 {est.localizacao_gaveta}
                            </span>
                          )}
                        </div>

                        <div className="text-xs text-slate-600 flex flex-wrap items-center gap-2">
                          <span>Cliente: <strong>{order.order?.customer?.name || "Não informado"}</strong></span>
                          {(() => {
                            const cleanP = getBaseSku(cleanSku(order.sku));
                            const mCfg = moldConfigsMap[cleanP];
                            const av = avaliarDisponibilidadeMolde(order, mCfg);
                            if (av.tipoMatch === "ajuste_bancada") {
                              return (
                                <span className="text-[11px] font-bold text-amber-900 bg-amber-100/90 border border-amber-300 px-2 py-0.5 rounded" title={av.instrucao}>
                                  Aro {av.aroSolicitado} ⚠️ [Molde {av.aroMolde}]
                                </span>
                              );
                            }
                            if (av.tipoMatch === "exato") {
                              return (
                                <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-1.5 py-0.2 rounded">
                                  Aro {av.aroSolicitado} (Molde Exato)
                                </span>
                              );
                            }
                            if (order.specs?.size) {
                              return <span className="text-indigo-700 bg-indigo-50 px-1 rounded font-bold">Aro {order.specs.size}</span>;
                            }
                            return null;
                          })()}
                          {order.specs?.stoneColor && <span className="text-pink-700 bg-pink-50 px-1 rounded">Pedra: {order.specs.stoneColor}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {/* Imprimir Filipeta deste Pedido */}
                      <button
                        type="button"
                        onClick={() => handlePrintFilipetas([order])}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                        title={`Imprimir Filipeta do Pedido #${order.order?.number || order.sku}`}
                      >
                        <Printer size={14} />
                      </button>
                      {isPending && onMoveStatus && (() => {
                        const cleanAro = order.specs?.size ? String(order.specs.size).trim() : null;
                        const aroStock = cleanAro && est.estoque_por_aro?.[cleanAro] ? est.estoque_por_aro[cleanAro] : null;
                        const canBruta = aroStock ? (Number(aroStock.bruta) || 0) > 0 : (est.cera_bruta > 0);
                        const canPrecravada = aroStock ? (Number(aroStock.precravada) || 0) > 0 : (est.cera_precravada > 0);

                        return (
                          <>
                            {canPrecravada && (
                              <button
                                type="button"
                                onClick={() => handleMoveOrderToInjetado(order, true, "precravada")}
                                disabled={movingId === order.id}
                                className="px-2.5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                title="Baixa 1 cera pré-cravada do estoque da gaveta e move para INJETADO"
                              >
                                <Gem size={13} className="text-purple-200" />
                                <span>Usar Cravada (-1)</span>
                              </button>
                            )}
                            {canBruta && (
                              <button
                                type="button"
                                onClick={() => handleMoveOrderToInjetado(order, true, "bruta")}
                                disabled={movingId === order.id}
                                className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                title="Baixa 1 cera do estoque da gaveta e move para INJETADO"
                              >
                                <Boxes size={13} className="text-emerald-200" />
                                <span>Usar Cera (-1)</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleMoveOrderToInjetado(order, false)}
                              disabled={movingId === order.id}
                              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                              title="Marcar peça como injetada (aguarda montagem na árvore)"
                            >
                              {movingId === order.id ? (
                                <RefreshCw size={13} className="animate-spin" />
                              ) : (
                                <Disc size={13} className="text-amber-200" />
                              )}
                              <span>Marcar Injetado</span>
                            </button>
                          </>
                        );
                      })()}

                      {order.status === "INJETADO" && onMoveStatus && (
                        <button
                          type="button"
                          onClick={() => handleMoveOrderToFundicao(order)}
                          disabled={movingId === order.id}
                          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                          title="Montar na árvore e avançar para Fundição"
                        >
                          {movingId === order.id ? (
                            <RefreshCw size={13} className="animate-spin" />
                          ) : (
                            <Flame size={13} className="text-teal-200" />
                          )}
                          <span>🌳 Montar na Árvore &rarr; Fundição</span>
                          <ArrowRight size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* =========================================================================
             VISÃO 2: CENTRAL GERAL DE SUBESTOQUE DE CERAS (TODOS OS MOLDES DA FÁBRICA)
             ========================================================================= */
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 rounded-xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                  <Boxes size={18} className="text-amber-400" />
                  <span>Almoxarifado & Buffer de Ceras de Moldes Físicos</span>
                </h2>
                <p className="text-xs text-slate-300 mt-0.5">
                  Monitore e alimente o estoque de ceras brutas e pré-cravadas de todos os modelos, mesmo sem pedidos em aberto.
                </p>
              </div>

              <div className="text-xs font-bold text-slate-300 flex items-center gap-3">
                <span className="bg-slate-700/60 px-3 py-1.5 rounded-lg border border-slate-600">
                  Mostrando <strong>{filteredCentralMoldes.length}</strong> de {allMoldesList.length} moldes
                </span>
              </div>
            </div>

            {filteredCentralMoldes.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400 space-y-2">
                <Boxes size={40} className="mx-auto stroke-1 text-slate-300" />
                <p className="text-sm font-semibold text-slate-700">Nenhum molde encontrado com este filtro.</p>
              </div>
            ) : viewModeCentral === "lista" ? (
              /* =========================================================================
                 MODO LISTA (OTIMIZADO PARA TABLETS E CELULARES — ALTA DENSIDADE & TOUCH)
                 ========================================================================= */
              <div className="space-y-2">
                {filteredCentralMoldes.map(molde => {
                  const skuPai = molde.skuPai;
                  const cat = findCatalogItem ? findCatalogItem(skuPai) : null;
                  const coverImg = cat?.image || cat?.image_url;
                  const name = cat?.name || molde.ficha_tecnica?.nome || `Produto ${skuPai}`;
                  const est = getSubestoqueCera(skuPai) || molde.subestoque || {};
                  const config = molde.config_borracha || extractConfigBorracha(molde, cat);
                  const ceraBruta = Number(est.cera_bruta) || 0;
                  const ceraPrecravada = Number(est.cera_precravada) || 0;
                  const total = ceraBruta + ceraPrecravada;
                  const demanda = demandMap[skuPai] || 0;
                  const modelo = config.tipo_modelo || resolveModeloJoia(skuPai, cat, molde);

                  return (
                    <div
                      key={skuPai}
                      className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-3 shadow-2xs transition-all flex flex-col xl:flex-row xl:items-center justify-between gap-3"
                    >
                      {/* IDENTIFICAÇÃO DO PRODUTO: FOTO + SKU PAI + NOME + BADGES */}
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-12 h-12 bg-slate-100 border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                          {coverImg ? (
                            <img src={coverImg} alt={skuPai} className="w-full h-full object-cover" />
                          ) : (
                            <Package size={20} className="text-slate-300" />
                          )}
                        </div>

                        <div className="space-y-0.5 min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openQuickProduct(skuPai)}
                              className="font-mono font-bold text-sm text-slate-900 hover:text-amber-600 hover:underline truncate shrink-0"
                              title="Abrir Ficha do Produto"
                            >
                              {skuPai}
                            </button>

                            <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                              {modelo}
                            </span>

                            {isModeloComMedida(modelo) && Array.isArray(config.aros_disponiveis) && config.aros_disponiveis.length > 0 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-50 text-amber-900 border border-amber-300 shrink-0" title={`Aros com molde: ${config.aros_disponiveis.join(', ')} (Tol: ±${config.tolerancia_ajuste_bancada ?? 1})`}>
                                💍 Aros: {config.aros_disponiveis.join(", ")}
                              </span>
                            )}

                            {isModeloComMedida(modelo) && est.estoque_por_aro && Object.keys(est.estoque_por_aro).length > 0 && (
                              <span className="inline-flex items-center gap-1 flex-wrap">
                                {Object.entries(est.estoque_por_aro)
                                  .filter(([_, q]) => (q.bruta > 0 || q.precravada > 0))
                                  .sort((a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0))
                                  .map(([aro, q]) => (
                                    <span
                                      key={aro}
                                      className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-950 border border-amber-300 shadow-2xs shrink-0"
                                      title={`Aro ${aro}: ${q.bruta} brutas, ${q.precravada} pré-cravadas`}
                                    >
                                      {aro}: <strong className="text-amber-800">{q.bruta}b</strong>{q.precravada > 0 && <span className="text-purple-700 font-black">+{q.precravada}c</span>}
                                    </span>
                                  ))}
                              </span>
                            )}

                            {est.localizacao_gaveta && (
                              <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-0.5 shrink-0">
                                <MapPin size={10} className="text-slate-400" /> {est.localizacao_gaveta}
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-slate-600 font-medium truncate">
                            {cat?.name || molde.ficha_tecnica?.nome || `Produto ${skuPai}`}
                          </p>
                        </div>
                      </div>

                      {/* BARRA INFERIOR / LATERAL: CONFIGURAÇÃO TÉCNICA + SALDOS DE ESTOQUE + AÇÕES */}
                      <div className="flex flex-wrap items-center justify-between xl:justify-end gap-3 pt-2.5 border-t border-slate-100 xl:border-t-0 xl:pt-0 shrink-0">
                        {/* CONFIGURAÇÃO TÉCNICA DA BORRACHA / CÓDIGO DA MÁQUINA */}
                        <div className="flex items-center gap-2 flex-wrap shrink-0">
                          <button
                            type="button"
                            onClick={() => setRubberConfigModal({ isOpen: true, skuPai, config })}
                            className={`px-2.5 py-1 rounded-lg border text-xs font-bold transition flex items-center gap-1.5 cursor-pointer select-none shrink-0 ${
                              config.codigo_maquina
                                ? "bg-amber-50 hover:bg-amber-100 text-amber-950 border-amber-300 shadow-2xs"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-500 border-dashed border-slate-300"
                            }`}
                            title="Clique para ver ou ajustar parâmetros da borracha na injetora"
                          >
                            <Settings2 size={13} className={config.codigo_maquina ? "text-amber-600" : "text-slate-400"} />
                            {config.codigo_maquina ? (
                              <span className="font-mono tracking-wider font-black">{config.codigo_maquina}</span>
                            ) : (
                              <span className="text-[11px] font-medium">+ Configurar</span>
                            )}
                          </button>

                          {(config.temperatura_tanque || config.temperatura_bico) && (
                            <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 flex items-center gap-0.5 shrink-0" title="Temperatura Tanque / Bico">
                              <Thermometer size={10} className="text-amber-600" />
                              {config.temperatura_tanque || "--"}° / {config.temperatura_bico || "--"}°
                            </span>
                          )}

                          {config.pedras_resumo && (
                            <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200 max-w-[140px] truncate shrink-0" title={config.pedras_resumo}>
                              💎 {config.pedras_resumo}
                            </span>
                          )}
                        </div>

                        {/* SALDOS DE ESTOQUE: BRUTAS E PRÉ-CRAVADAS */}
                        <div className="flex items-center gap-2 flex-wrap shrink-0">
                          {/* Cera Bruta Badge */}
                          <div
                            className="flex items-center gap-1.5 bg-amber-50/70 border border-amber-200 rounded-lg px-2.5 py-1 shadow-2xs shrink-0"
                            title="Ceras brutas disponíveis na gaveta"
                          >
                            <Flame size={13} className="text-amber-600" />
                            <span className="text-[10px] font-bold text-amber-800 uppercase">Bruta:</span>
                            <span className="text-sm font-black text-amber-950 min-w-[18px] text-center">
                              {ceraBruta}
                            </span>
                          </div>

                          {/* Cera Pré-Cravada Badge */}
                          <div
                            className="flex items-center gap-1.5 bg-purple-50/70 border border-purple-200 rounded-lg px-2.5 py-1 shadow-2xs shrink-0"
                            title="Ceras pré-cravadas disponíveis na gaveta"
                          >
                            <Gem size={13} className="text-purple-600" />
                            <span className="text-[10px] font-bold text-purple-800 uppercase">Cravada:</span>
                            <span className="text-sm font-black text-purple-950 min-w-[18px] text-center">
                              {ceraPrecravada}
                            </span>
                          </div>

                          {/* Demanda & Atalhos */}
                          <div className="flex items-center gap-1.5 shrink-0 pl-1 border-l border-slate-200">
                            {demanda > 0 ? (
                              <span className="bg-amber-100 text-amber-900 border border-amber-300 text-xs font-black px-2 py-1 rounded-lg shrink-0">
                                {demanda} fila
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400 font-semibold px-1 shrink-0">
                                0 fila
                              </span>
                            )}

                            <button
                              type="button"
                              onClick={() => setFundirModalItem({
                                sku: skuPai,
                                name,
                                image: coverImg,
                                gaveta: est.localizacao_gaveta || "",
                                cera_bruta: ceraBruta,
                                cera_precravada: ceraPrecravada,
                                estoque_por_aro: est.estoque_por_aro || {},
                                config_borracha: config || {},
                                tipo_modelo: modelo || ""
                              })}
                              className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-lg font-bold text-xs transition flex items-center gap-1 shadow-2xs cursor-pointer active:scale-95 shrink-0"
                              title="Solicitar produção de estoque para este molde"
                            >
                              <Flame size={12} className="text-amber-600" />
                              <span>⚡ Pedir Estoque</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => setHistoryModal({
                                isOpen: true,
                                skuPai,
                                historico: est.historico || []
                              })}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition shrink-0"
                              title="Histórico de movimentações"
                            >
                              <History size={15} />
                            </button>

                            <button
                              type="button"
                              onClick={() => openAdjustModal(skuPai, est, modelo)}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition shrink-0"
                              title="Inventário / Ajuste de Saldo Físico"
                            >
                              <Edit3 size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* =========================================================================
                 MODO GRADE DE CARDS (VISÃO DETALHADA)
                 ========================================================================= */
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredCentralMoldes.map(molde => {
                  const skuPai = molde.skuPai;
                  const cat = findCatalogItem ? findCatalogItem(skuPai) : null;
                  const coverImg = cat?.image || cat?.image_url;
                  const name = cat?.name || molde.ficha_tecnica?.nome || `Produto ${skuPai}`;
                  const est = getSubestoqueCera(skuPai) || molde.subestoque || {};
                  const config = molde.config_borracha || extractConfigBorracha(molde, cat);
                  const ceraBruta = Number(est.cera_bruta) || 0;
                  const ceraPrecravada = Number(est.cera_precravada) || 0;
                  const total = ceraBruta + ceraPrecravada;
                  const demanda = demandMap[skuPai] || 0;
                  const modelo = config.tipo_modelo || resolveModeloJoia(skuPai, cat, molde);

                  return (
                    <div
                      key={skuPai}
                      className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow transition-all flex flex-col justify-between gap-4"
                    >
                      {/* TOPO DO CARD */}
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 bg-slate-100 border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                          {coverImg ? (
                            <img src={coverImg} alt={skuPai} className="w-full h-full object-cover" />
                          ) : (
                            <Package size={24} className="text-slate-300" />
                          )}
                        </div>

                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => openQuickProduct(skuPai)}
                              className="font-mono font-bold text-sm text-slate-900 hover:text-amber-600 hover:underline truncate"
                              title="Abrir Ficha do Produto"
                            >
                              {skuPai}
                            </button>

                            {demanda > 0 ? (
                              <span className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0">
                                {demanda} em fila
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-400 font-semibold shrink-0">
                                0 pedidos
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-slate-600 font-medium line-clamp-1">
                            {cat?.name || molde.ficha_tecnica?.nome || `Produto ${skuPai}`}
                          </p>

                          {/* MODELO + CÓDIGO DA MÁQUINA + GAVETA */}
                          <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                            <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                              {modelo}
                            </span>

                            <button
                              type="button"
                              onClick={() => setRubberConfigModal({ isOpen: true, skuPai, config })}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition flex items-center gap-1 cursor-pointer ${
                                config.codigo_maquina
                                  ? "bg-amber-100 text-amber-950 border-amber-300 hover:bg-amber-200"
                                  : "bg-slate-100 text-slate-500 border-dashed border-slate-300 hover:bg-slate-200"
                              }`}
                              title="Clique para ver ou ajustar parâmetros da borracha na injetora"
                            >
                              <Settings2 size={10} className={config.codigo_maquina ? "text-amber-700" : "text-slate-400"} />
                              {config.codigo_maquina ? (
                                <span className="font-mono font-black tracking-wider">⚙️ {config.codigo_maquina}</span>
                              ) : (
                                <span>+ Configurar</span>
                              )}
                            </button>

                            {isModeloComMedida(modelo) && Array.isArray(config.aros_disponiveis) && config.aros_disponiveis.length > 0 && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 inline-block" title={`Aros com molde: ${config.aros_disponiveis.join(', ')}`}>
                                💍 Aros: {config.aros_disponiveis.join(", ")}
                              </span>
                            )}

                            {isModeloComMedida(modelo) && est.estoque_por_aro && Object.keys(est.estoque_por_aro).length > 0 && (
                              <span className="inline-flex items-center gap-1 flex-wrap">
                                {Object.entries(est.estoque_por_aro)
                                  .filter(([_, q]) => (q.bruta > 0 || q.precravada > 0))
                                  .sort((a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0))
                                  .map(([aro, q]) => (
                                    <span
                                      key={aro}
                                      className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-950 border border-amber-300 shadow-2xs"
                                      title={`Aro ${aro}: ${q.bruta} brutas, ${q.precravada} pré-cravadas`}
                                    >
                                      {aro}: <strong className="text-amber-800">{q.bruta}b</strong>{q.precravada > 0 && <span className="text-purple-700 font-black">+{q.precravada}c</span>}
                                    </span>
                                  ))}
                              </span>
                            )}

                            {est.localizacao_gaveta && (
                              <span className="text-[11px] text-slate-500 flex items-center gap-1">
                                <MapPin size={11} className="text-slate-400" />
                                <span>{est.localizacao_gaveta}</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* PAINEL DE SALDOS DE ESTOQUE */}
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          {/* Cera Bruta */}
                          <div className="bg-white border border-amber-200 rounded-lg p-2.5 shadow-2xs">
                            <span className="text-[10px] font-bold text-amber-800 uppercase flex items-center gap-1">
                              <Flame size={11} className="text-amber-600" /> Bruta
                            </span>
                            <span className="text-sm font-black text-amber-950 mt-1 block">
                              {ceraBruta} un
                            </span>
                          </div>

                          {/* Cera Pré-Cravada */}
                          <div className="bg-white border border-purple-200 rounded-lg p-2.5 shadow-2xs">
                            <span className="text-[10px] font-bold text-purple-800 uppercase flex items-center gap-1">
                              <Gem size={11} className="text-purple-600" /> Pré-Cravada
                            </span>
                            <span className="text-sm font-black text-purple-950 mt-1 block">
                              {ceraPrecravada} un
                            </span>
                          </div>
                        </div>

                        {/* STATUS DO BUFFER */}
                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200/80">
                          <span className="text-slate-500 font-medium">Total em gaveta:</span>
                          <span className={`font-black px-2 py-0.5 rounded text-[11px] ${
                            total > 0 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                          }`}>
                            {total} {total === 1 ? "cera" : "ceras"}
                          </span>
                        </div>
                      </div>

                      {/* RODAPÉ COM AÇÕES E HISTÓRICO */}
                      <div className="flex items-center justify-between pt-1 text-xs gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setHistoryModal({
                            isOpen: true,
                            skuPai,
                            historico: est.historico || []
                          })}
                          className="text-slate-500 hover:text-slate-800 flex items-center gap-1 font-semibold text-[11px]"
                          title="Ver histórico de movimentações deste molde"
                        >
                          <History size={13} />
                          <span>Histórico ({est.historico?.length || 0})</span>
                        </button>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setFundirModalItem({
                              sku: skuPai,
                              name,
                              image: coverImg,
                              gaveta: est.localizacao_gaveta || "",
                              cera_bruta: ceraBruta,
                              cera_precravada: ceraPrecravada,
                              estoque_por_aro: est.estoque_por_aro || {},
                              config_borracha: config || {},
                              tipo_modelo: modelo || ""
                            })}
                            className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 rounded-lg font-bold text-[11px] shadow-sm flex items-center gap-1 transition cursor-pointer active:scale-95"
                            title="Solicitar produção de estoque para este molde"
                          >
                            <Flame size={12} className="text-amber-600" />
                            <span>⚡ Pedir Estoque</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => openAdjustModal(skuPai, est, modelo)}
                            className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-700 rounded-lg border border-slate-300 font-bold text-[11px] shadow-sm flex items-center gap-1 cursor-pointer"
                          >
                            <Edit3 size={12} />
                            <span>Ajuste</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* =========================================================================
          MODAL DE AJUSTE / INVENTÁRIO FÍSICO DO SUBESTOQUE
         ========================================================================= */}
      {/* =========================================================================
          MODAL DE AJUSTE / INVENTÁRIO FÍSICO DO SUBESTOQUE (COM GRADE POR ARO)
         ========================================================================= */}
      {adjustModal && (() => {
        const isGrade = adjustModal.mode === "grade";
        const gradeEntries = Object.entries(adjustModal.estoquePorAro || {}).sort(
          (a, b) => (parseInt(a[0], 10) || 0) - (parseInt(b[0], 10) || 0)
        );
        const sumBruta = gradeEntries.reduce((acc, [_, v]) => acc + (parseInt(v.bruta, 10) || 0), 0);
        const sumPrecravada = gradeEntries.reduce((acc, [_, v]) => acc + (parseInt(v.precravada, 10) || 0), 0);
        const sumTotal = sumBruta + sumPrecravada;

        const globalTotal = (parseInt(adjustModal.ceraBruta, 10) || 0) + (parseInt(adjustModal.ceraPrecravada, 10) || 0);
        const finalTotalToSave = isGrade ? sumTotal : globalTotal;

        return (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-150 my-auto">
              <div className="p-4 md:p-5 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-slate-800 text-white rounded-lg">
                    <Edit3 size={16} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">
                      Inventário / Ajuste Físico de Ceras
                    </h3>
                    <p className="text-[11px] text-slate-500 font-mono font-bold">
                      {adjustModal.skuPai}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAdjustModal(null)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-4 text-xs max-h-[75vh] overflow-y-auto">
                {/* SELETOR DE MODO (Grade por Aro vs Saldo Geral) */}
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setAdjustModal(prev => ({ ...prev, mode: "grade" }))}
                    className={`flex-1 py-1.5 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer select-none ${
                      isGrade
                        ? "bg-white text-slate-900 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <span>💍 Grade por Aro / Medida</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdjustModal(prev => ({ ...prev, mode: "global" }))}
                    className={`flex-1 py-1.5 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer select-none ${
                      !isGrade
                        ? "bg-white text-slate-900 shadow-xs"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <span>📦 Saldo Geral</span>
                  </button>
                </div>

                {/* MODO 1: GRADE DETALHADA POR ARO */}
                {isGrade ? (
                  <div className="space-y-3">
                    {/* Resumo Dinâmico da Grade */}
                    <div className="p-3 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-purple-500/10 border border-amber-200 rounded-xl flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                          Total Calculado na Gaveta
                        </span>
                        <span className="text-base font-black text-slate-900">
                          {sumTotal} <span className="text-xs font-bold text-slate-600">ceras</span>
                        </span>
                      </div>
                      <div className="text-right text-[11px] font-semibold text-slate-600">
                        <span className="text-amber-800 font-bold">{sumBruta} brutas</span> •{" "}
                        <span className="text-purple-800 font-bold">{sumPrecravada} pré-cravadas</span>
                      </div>
                    </div>

                    {/* Lista / Tabela de Aros com Steppers */}
                    <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/50 p-2 space-y-2">
                      {gradeEntries.length === 0 ? (
                        <div className="p-4 text-center text-slate-400 font-medium">
                          Nenhum aro adicionado ainda. Use o campo abaixo para adicionar.
                        </div>
                      ) : (
                        gradeEntries.map(([aro, val]) => (
                          <div
                            key={aro}
                            className="bg-white border border-slate-200 rounded-lg p-2 flex items-center justify-between gap-2 shadow-2xs"
                          >
                            <div className="w-16 shrink-0">
                              <span className="font-black text-xs text-indigo-950 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-md block text-center">
                                Aro {aro}
                              </span>
                            </div>

                            {/* Bruta Stepper */}
                            <div className="flex-1 flex flex-col items-center">
                              <span className="text-[9px] font-bold text-amber-800 mb-0.5">🕯️ Bruta</span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          bruta: Math.max(0, (parseInt(prev.estoquePorAro[aro]?.bruta, 10) || 0) - 1)
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 font-black text-xs flex items-center justify-center cursor-pointer select-none active:scale-95"
                                >
                                  -
                                </button>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={val.bruta !== undefined && val.bruta !== null ? val.bruta : 0}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, "");
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          bruta: v === "" ? "" : parseInt(v, 10)
                                        }
                                      }
                                    }));
                                  }}
                                  onBlur={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          bruta: Math.max(0, parseInt(prev.estoquePorAro[aro]?.bruta, 10) || 0)
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-10 text-center font-black text-xs py-0.5 border border-slate-300 rounded focus:ring-1 focus:ring-amber-500 focus:outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          bruta: (parseInt(prev.estoquePorAro[aro]?.bruta, 10) || 0) + 1
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 font-black text-xs flex items-center justify-center cursor-pointer select-none active:scale-95"
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            {/* Pré-cravada Stepper */}
                            <div className="flex-1 flex flex-col items-center">
                              <span className="text-[9px] font-bold text-purple-800 mb-0.5">💎 Cravada</span>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          precravada: Math.max(0, (parseInt(prev.estoquePorAro[aro]?.precravada, 10) || 0) - 1)
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 font-black text-xs flex items-center justify-center cursor-pointer select-none active:scale-95"
                                >
                                  -
                                </button>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={val.precravada !== undefined && val.precravada !== null ? val.precravada : 0}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, "");
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          precravada: v === "" ? "" : parseInt(v, 10)
                                        }
                                      }
                                    }));
                                  }}
                                  onBlur={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          precravada: Math.max(0, parseInt(prev.estoquePorAro[aro]?.precravada, 10) || 0)
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-10 text-center font-black text-xs py-0.5 border border-slate-300 rounded focus:ring-1 focus:ring-purple-500 focus:outline-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAdjustModal(prev => ({
                                      ...prev,
                                      estoquePorAro: {
                                        ...prev.estoquePorAro,
                                        [aro]: {
                                          ...prev.estoquePorAro[aro],
                                          precravada: (parseInt(prev.estoquePorAro[aro]?.precravada, 10) || 0) + 1
                                        }
                                      }
                                    }));
                                  }}
                                  className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 font-black text-xs flex items-center justify-center cursor-pointer select-none active:scale-95"
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            {/* Subtotal e Remover */}
                            <div className="w-16 shrink-0 flex items-center justify-end gap-1 text-right">
                              <span className="font-mono font-bold text-xs text-slate-800">
                                {(parseInt(val.bruta, 10) || 0) + (parseInt(val.precravada, 10) || 0)} un
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setAdjustModal(prev => {
                                    const next = { ...prev.estoquePorAro };
                                    delete next[aro];
                                    return { ...prev, estoquePorAro: next };
                                  });
                                }}
                                className="text-slate-300 hover:text-rose-500 p-0.5 rounded transition cursor-pointer"
                                title={`Remover Aro ${aro}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Adicionar Novo Aro */}
                    <div className="pt-2 border-t border-slate-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Novo aro (ex: 15, 23, 27...)"
                          value={adjustModal.newAroInput || ""}
                          onChange={(e) => setAdjustModal(prev => ({ ...prev, newAroInput: e.target.value.replace(/\D/g, "") }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const aro = adjustModal.newAroInput?.trim();
                              if (aro && !adjustModal.estoquePorAro[aro]) {
                                setAdjustModal(prev => ({
                                  ...prev,
                                  newAroInput: "",
                                  estoquePorAro: { ...prev.estoquePorAro, [aro]: { bruta: 0, precravada: 0 } }
                                }));
                              }
                            }
                          }}
                          className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const aro = adjustModal.newAroInput?.trim();
                            if (aro && !adjustModal.estoquePorAro[aro]) {
                              setAdjustModal(prev => ({
                                ...prev,
                                newAroInput: "",
                                estoquePorAro: { ...prev.estoquePorAro, [aro]: { bruta: 0, precravada: 0 } }
                              }));
                            }
                          }}
                          className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-lg font-bold text-xs cursor-pointer active:scale-95"
                        >
                          + Adicionar Aro
                        </button>
                      </div>

                      {/* Sugestões rápidas de aros comuns */}
                      {(() => {
                        const common = ["12", "14", "16", "18", "20", "22", "24", "26", "28"].filter(
                          a => !adjustModal.estoquePorAro[a]
                        );
                        if (common.length === 0) return null;
                        return (
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-[10px] text-slate-400 font-semibold">Atalhos:</span>
                            {common.map(aro => (
                              <button
                                key={aro}
                                type="button"
                                onClick={() => setAdjustModal(prev => ({
                                  ...prev,
                                  estoquePorAro: { ...prev.estoquePorAro, [aro]: { bruta: 0, precravada: 0 } }
                                }))}
                                className="text-[10px] font-bold text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded cursor-pointer transition"
                              >
                                +{aro}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  /* MODO 2: ENTRADA GLOBAL SIMPLES */
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        🕯️ Cera Bruta (Injetada)
                      </label>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setAdjustModal(prev => ({
                            ...prev,
                            ceraBruta: Math.max(0, (parseInt(prev.ceraBruta, 10) || 0) - 1)
                          }))}
                          className="w-9 h-9 rounded-lg bg-slate-200 hover:bg-slate-300 font-black text-sm flex items-center justify-center active:scale-95 cursor-pointer select-none"
                          title="Diminuir 1"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={adjustModal.ceraBruta !== undefined && adjustModal.ceraBruta !== null ? adjustModal.ceraBruta : ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            setAdjustModal(prev => ({ ...prev, ceraBruta: val }));
                          }}
                          onBlur={() => {
                            if (adjustModal.ceraBruta === "" || isNaN(parseInt(adjustModal.ceraBruta, 10))) {
                              setAdjustModal(prev => ({ ...prev, ceraBruta: 0 }));
                            } else {
                              setAdjustModal(prev => ({ ...prev, ceraBruta: Math.max(0, parseInt(prev.ceraBruta, 10)) }));
                            }
                          }}
                          className="flex-1 min-w-0 px-2 py-2 bg-white border border-slate-300 rounded-lg text-sm font-black text-center text-slate-900 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setAdjustModal(prev => ({
                            ...prev,
                            ceraBruta: (parseInt(prev.ceraBruta, 10) || 0) + 1
                          }))}
                          className="w-9 h-9 rounded-lg bg-slate-200 hover:bg-slate-300 font-black text-sm flex items-center justify-center active:scale-95 cursor-pointer select-none"
                          title="Aumentar 1"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        💎 Cera Pré-Cravada
                      </label>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setAdjustModal(prev => ({
                            ...prev,
                            ceraPrecravada: Math.max(0, (parseInt(prev.ceraPrecravada, 10) || 0) - 1)
                          }))}
                          className="w-9 h-9 rounded-lg bg-slate-200 hover:bg-slate-300 font-black text-sm flex items-center justify-center active:scale-95 cursor-pointer select-none"
                          title="Diminuir 1"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={adjustModal.ceraPrecravada !== undefined && adjustModal.ceraPrecravada !== null ? adjustModal.ceraPrecravada : ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            setAdjustModal(prev => ({ ...prev, ceraPrecravada: val }));
                          }}
                          onBlur={() => {
                            if (adjustModal.ceraPrecravada === "" || isNaN(parseInt(adjustModal.ceraPrecravada, 10))) {
                              setAdjustModal(prev => ({ ...prev, ceraPrecravada: 0 }));
                            } else {
                              setAdjustModal(prev => ({ ...prev, ceraPrecravada: Math.max(0, parseInt(prev.ceraPrecravada, 10)) }));
                            }
                          }}
                          className="flex-1 min-w-0 px-2 py-2 bg-white border border-slate-300 rounded-lg text-sm font-black text-center text-slate-900 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setAdjustModal(prev => ({
                            ...prev,
                            ceraPrecravada: (parseInt(prev.ceraPrecravada, 10) || 0) + 1
                          }))}
                          className="w-9 h-9 rounded-lg bg-slate-200 hover:bg-slate-300 font-black text-sm flex items-center justify-center active:scale-95 cursor-pointer select-none"
                          title="Aumentar 1"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    📍 Gaveta / Posição Física na Fábrica
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Gaveta G-04, Caixa Bicos..."
                    value={adjustModal.gaveta}
                    onChange={(e) => setAdjustModal(prev => ({ ...prev, gaveta: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 uppercase focus:bg-white focus:ring-2 focus:ring-slate-800"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    📝 Motivo do Ajuste (Auditoria)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Contagem física semanal, Cera quebrou na gaveta..."
                    value={adjustModal.motivo}
                    onChange={(e) => setAdjustModal(prev => ({ ...prev, motivo: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-slate-800"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
                <div className="text-[11px] text-slate-500 font-semibold">
                  Saldo: <strong className="text-slate-800 font-black">{finalTotalToSave} un</strong>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAdjustModal(null)}
                    className="px-4 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-lg border border-slate-200 font-bold text-xs cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAdjustModal}
                    disabled={stockUpdatingId === adjustModal.skuPai}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-xs shadow flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {stockUpdatingId === adjustModal.skuPai ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                    <span>Salvar Saldo Real</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* =========================================================================
          MODAL DE HISTÓRICO DE AUDITORIA DE MOVIMENTAÇÕES
         ========================================================================= */}
      {historyModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden max-h-[85vh] flex flex-col animate-in fade-in zoom-in duration-150">
            <div className="p-4 md:p-5 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-slate-800 text-white rounded-lg">
                  <History size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">
                    Histórico de Movimentações de Cera
                  </h3>
                  <p className="text-[11px] text-slate-500 font-mono font-bold">
                    {historyModal.skuPai}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setHistoryModal(null)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto custom-scrollbar flex-1">
              {(!historyModal.historico || historyModal.historico.length === 0) ? (
                <div className="p-8 text-center text-slate-400 text-xs">
                  Nenhuma movimentação registrada para este molde ainda.
                </div>
              ) : (
                <div className="divide-y divide-slate-100 text-xs">
                  {historyModal.historico.map((h, i) => (
                    <div key={i} className="py-2.5 flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold text-slate-800">
                          {h.motivo || h.tipo || "Movimentação"}
                        </div>
                        <div className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                          <span>{new Date(h.data).toLocaleString("pt-BR")}</span>
                          <span>•</span>
                          <span>Por: <strong>{h.usuario || "Operador"}</strong></span>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="font-mono font-bold text-xs flex items-center gap-2 justify-end">
                          {h.delta_bruta !== 0 && (
                            <span className={h.delta_bruta > 0 ? "text-emerald-600" : "text-rose-600"}>
                              Bruta: {h.delta_bruta > 0 ? `+${h.delta_bruta}` : h.delta_bruta}
                            </span>
                          )}
                          {h.delta_precravada !== 0 && (
                            <span className={h.delta_precravada > 0 ? "text-purple-600" : "text-rose-600"}>
                              Cravada: {h.delta_precravada > 0 ? `+${h.delta_precravada}` : h.delta_precravada}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          Saldo após: {h.saldo_bruta} brutas | {h.saldo_precravada} cravadas
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end shrink-0">
              <button
                type="button"
                onClick={() => setHistoryModal(null)}
                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-bold text-xs cursor-pointer"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÃO TÉCNICA DA BORRACHA */}
      {rubberConfigModal?.isOpen && (
        <ConfigBorrachaModal
          isOpen={rubberConfigModal.isOpen}
          onClose={() => setRubberConfigModal({ isOpen: false, skuPai: null, config: null })}
          skuPai={rubberConfigModal.skuPai}
          initialConfig={rubberConfigModal.config}
          userName={user?.name || "Operador"}
          allMoldes={allMoldesList}
          onSuccess={(savedConfig) => {
            const targetPai = getBaseSku(cleanSku(rubberConfigModal.skuPai));
            setAllMoldesList(prev => {
              const exists = prev.some(m => getBaseSku(cleanSku(m.skuPai)) === targetPai);
              if (!exists) {
                return [...prev, {
                  skuPai: targetPai,
                  temMolde: true,
                  config_borracha: savedConfig,
                  ficha_tecnica: { config_borracha: savedConfig }
                }];
              }
              return prev.map(m => {
                const mPai = getBaseSku(cleanSku(m.skuPai));
                if (mPai === targetPai) {
                  return { 
                    ...m, 
                    config_borracha: savedConfig,
                    ficha_tecnica: {
                      ...(m.ficha_tecnica || {}),
                      config_borracha: savedConfig
                    }
                  };
                }
                return m;
              });
            });
            // Recarrega silenciosamente para sincronização total
            loadAllMoldes();
          }}
        />
      )}

      {/* MODAL DE CADASTRO DE NOVO MOLDE / BORRACHA */}
      {isNovoMoldeModalOpen && (
        <NovoMoldeModal
          isOpen={isNovoMoldeModalOpen}
          onClose={() => setIsNovoMoldeModalOpen(false)}
          allMoldes={allMoldesList}
          user={user}
          onSuccess={({ skuPai, openConfigImmediately }) => {
            loadAllMoldes();
            if (openConfigImmediately) {
              setRubberConfigModal({ isOpen: true, skuPai, config: null });
            }
          }}
        />
      )}

      {/* MODAL DE ENVIO DE CERAS PARA FUNDIÇÃO */}
      {fundirModalItem && (
        <EnviarCerasFundicaoModal
          isOpen={!!fundirModalItem}
          onClose={() => setFundirModalItem(null)}
          item={fundirModalItem}
          user={user}
          onSuccess={() => {
            loadAllMoldes();
          }}
        />
      )}

      {/* MODAL DE TEXTO / FILIPETAS DE PRODUÇÃO (FALLBACK E CÓPIA) */}
      {printContent && (
        <TextModal
          title="Filipetas de Produção"
          content={printContent}
          onClose={() => setPrintContent(null)}
        />
      )}

    </div>
  );
}
